import { useSyncExternalStore } from "react";
import {
  createPayroll as requestPayrollBuild,
  deletePayroll as requestPayrollDelete,
  fetchDataPullStatus,
  fetchPayflowEvents,
  fetchPayroll,
  fetchPayrollDrift,
  fetchPayrollRegisters,
  fetchPayrollState,
  fetchRules,
  listPayrolls,
  postPayrollAction,
  saveRules,
  startDataPull as requestDataPull,
  fetchDateCoverage,
  PayflowApiError,
  type DataPullStatus,
  type DataPullWindow,
  type DateCoverage,
  type DriftReport,
  type FrozenStagesResponse,
  type PayrollActionRequest,
  type PayrollDecisionState,
  type PayrollRegister,
  type PayConstants,
  type PayPeriodConfig,
  type PayflowRules,
  type PayrollDay,
  type PayrollDoc,
  type PayrollSummary,
  type StoredPayrollRegister,
} from "./payflowApi";

/**
 * PayFlow — shared store.
 *
 * The PayFlow admin screen (ManagePayFlow) and the manager review screen
 * (PayFlow, user side) live on separate routes, so they can't share React
 * state directly. This store lets both read the same payroll data and the same
 * manager decisions, and stay in sync when either side acts.
 *
 * Ownership is split:
 *   • payrolls (periods, days, rows) and the payroll rules are SERVER owned —
 *     fetched on first use, refreshed via refreshFromServer()
 *   • decisions (adjustments, reviews, approvals, overrides, pools, staff
 *     accounts, audit log) still live in localStorage, keyed by the server's
 *     payroll ids
 */

/* ── Types ────────────────────────────────────────────────────────────── */

// Real POS figures for one shift, when the pipeline found a sales record.
export interface ShiftSales {
  netSales: number;
  cash: number;
  ccTips: number;
  autoGrats: number;
}

export interface EmployeeShift {
  id: string;
  // Present (1, 2, …) only on an employee's 2nd+ shift of the same day.
  seq?: number;
  name: string;
  role: string;
  // Bartender bar assignment from Scheduling (Main Bar / Parlour / Patio).
  // Drives tip pooling — each bar is its own pool.
  location?: string;
  // Minutes-from-midnight, so comparisons + hour totals are trivial.
  //
  // Each pair is absent when that side of the match is missing: no schedule (nobody
  // scheduled the shift, or the pipeline never retrieved it — it drops inactive
  // employees), or no clock record (scheduled but never clocked in). Read them through
  // scheduleSpan()/workedSpan() below rather than defaulting them — a `?? 0` schedules
  // the shift at midnight — and show them as "—".
  schedStart?: number;
  schedEnd?: number;
  actualIn?: number;
  actualOut?: number;
  sales?: ShiftSales;
}

export interface Day {
  index: number; // 0..13, stable id within the pay period
  weekday: string;
  dateLabel: string;
  dateISO: string; // yyyy-mm-dd, for stat-holiday matching
  rows: EmployeeShift[];
}

export interface Ymd {
  year: number;
  monthIndex: number;
  day: number;
}

// Admin-controlled lifecycle stage. The displayed status is "Pending" whenever
// the period still has unresolved major variances, otherwise it reflects stage.
export type PayPeriodStage = "ready" | "processed";
export type PayPeriodStatus =
  | "pending"
  | "ready"
  | "approved"
  | "tipsverified"
  | "processed";

export interface PayPeriod {
  id: string; // server payroll id — the period's ending ISO date
  name: string;
  start: Ymd; // first day of the period
  endISO: string; // last day, yyyy-mm-dd (same value as id)
  stage: PayPeriodStage;
  statusDate: string; // display date of the last status change
  daysLoaded: boolean; // the day/row detail has been fetched from the server
  // The accountant's decisions for this period have been fetched. Separate from
  // `daysLoaded` because they are two requests that can land in either order, and a
  // screen that renders on days alone shows hours with no adjustments applied.
  decisionsLoaded: boolean;
  days: Day[];
  // Defects the server found in the source data while assembling this payroll.
  // Known only once the detail has been fetched, so empty until then.
  warnings: string[];
  // The rules THIS payroll was built under. Every figure on the period screens is
  // computed from these, not from the live rules, so editing a tip rate today cannot
  // change what a finished payroll says. Undefined on payrolls built before rules
  // were snapshotted; those fall back to the live rules and say so.
  rules?: PayflowRules;
}

// Payroll control settings.

// Ontario public-holiday template. Fixed-date holidays carry their month/day;
// the rest are admin-assigned each year (their date moves).
export interface OntarioHolidayDef {
  key: string;
  name: string;
  fixedMonth?: number; // 1-12
  fixedDay?: number;
}

export const ONTARIO_HOLIDAYS: OntarioHolidayDef[] = [
  { key: "newyears", name: "New Years Day", fixedMonth: 1, fixedDay: 1 },
  { key: "family", name: "Family Day" },
  { key: "goodfriday", name: "Good Friday" },
  { key: "victoria", name: "Victoria Day" },
  { key: "canada", name: "Canada Day", fixedMonth: 7, fixedDay: 1 },
  { key: "labour", name: "Labour Day" },
  { key: "thanksgiving", name: "Thanksgiving" },
  { key: "christmas", name: "Christmas Day", fixedMonth: 12, fixedDay: 25 },
  { key: "boxing", name: "Boxing Day", fixedMonth: 12, fixedDay: 26 },
];

// A committed (locked) holiday date for a given calendar year.
export interface CommittedHoliday {
  key: string;
  name: string;
  date: string; // ISO yyyy-mm-dd
}
// year (as string) → the locked list of that year's holiday dates.
export type CommittedHolidays = Record<string, CommittedHoliday[]>;

export interface BreakRule {
  overHours: number; // shift longer than this …
  deductHours: number; // … deducts this many unpaid-break hours
}

// Tip-calculation percentages. Committed (locked) like stat holidays; these
// feed the tip-deduction math later.
export interface TipRates {
  creditCardFee: number; // % deducted from CC tips to the house
  tipPool: number; // % of sales bar/servers pay to the house pool
  hostFoodRunners: number; // % of sales paid to the host/food-runner pool
  serverToBar: number; // % servers pay to bartenders
  barBackPool: number; // % bartenders pay to the barback pool
}

export interface TipRatesLock {
  by: string;
  at: string;
}

// An entry in the payroll audit log (mirrors the Manage Profiles access log).
export interface AuditEntry {
  at: string; // ISO timestamp ("When")
  by: string; // user name/email ("Action By")
  action: string; // what happened (subject folded in)
  reason: string;
  ip: string; // captured server-side in production
  userAgent: string;
}

// ISO date for a fixed holiday in a given year (null for admin-assigned ones).
export const fixedHolidayISO = (def: OntarioHolidayDef, year: number): string | null =>
  def.fixedMonth && def.fixedDay
    ? `${year}-${String(def.fixedMonth).padStart(2, "0")}-${String(def.fixedDay).padStart(2, "0")}`
    : null;

// Draft key for an admin-assigned holiday date, scoped to a year so that on a
// new calendar year all assignments reset (the prior keys simply don't match).
export const holidayDraftKey = (year: number, holidayKey: string): string =>
  `${year}:${holidayKey}`;

// A manager review confirms the worked clock-in/out for an extraordinary
// shift. The confirmed times (minutes-from-midnight, out may exceed 1440 for
// overnight) become the authoritative worked times for net-hours.
export interface Review {
  confirmedIn: number;
  confirmedOut: number;
  reason: string;
  confirmedBy?: string; // name/email of the manager who confirmed
  confirmedAt?: string; // ISO timestamp of when it was confirmed
}

// Variance beyond this (on either clock-in or clock-out) is "extraordinary"
// and must be signed off by a manager before any deduction is applied.
export const TOLERANCE_MINUTES = 30;

// Hard closing cap: nobody is paid past 2:30 AM. Any clock-out later than this
// is silently pulled back to 2:30 AM (minutes-from-midnight, next day = 1590)
// before tolerance/variance logic runs — so it never triggers a manager review.
export const CLOSING_CAP_MINUTES = 26 * 60 + 30; // 1590 = 2:30 AM next day

/* ── Dates ────────────────────────────────────────────────────────────── */

const WEEKDAYS = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// ISO yyyy-mm-dd → "July 1, 2026" for display.
export const fmtISODate = (iso: string): string => {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return `${MONTHS[m - 1]} ${d}, ${y}`;
};

// ISO yyyy-mm-dd → calendar parts (local, never UTC-shifted).
export const ymdFromISO = (iso: string): Ymd => {
  const [y, m, d] = iso.split("-").map(Number);
  return { year: y, monthIndex: m - 1, day: d };
};

const weekdayOfISO = (iso: string): string => {
  const { year, monthIndex, day } = ymdFromISO(iso);
  return WEEKDAYS[new Date(year, monthIndex, day).getDay()];
};

// ISO timestamp → the "June 1, 2026" display format used for status dates.
const fmtTimestampDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};

/* ── Defaults (used until the server's rules arrive) ──────────────────── */

const DEFAULT_BREAK_RULES: BreakRule[] = [
  { overHours: 4, deductHours: 0.5 },
  { overHours: 10, deductHours: 1 },
];

const DEFAULT_TIP_RATES: TipRates = {
  creditCardFee: 2,
  tipPool: 5.5,
  hostFoodRunners: 1,
  serverToBar: 1,
  barBackPool: 1,
};

// Roles eligible for the FOH premium. Admin-editable & commit-locked in Payroll
// Rules; seeded to the historical default (Host / Food Runner).
const DEFAULT_FOH_ROLES: string[] = ["Host", "Food Runner"];

const DEFAULT_PAY_CONSTANTS: PayConstants = {
  baseHourlyRate: 17.6,
  kitchenTipPoolPerHour: 2.4,
  fohPremiumPerHour: 1,
  vacationPayRate: 0.04,
};

// Pay periods run Monday to Sunday, so anchorEnd is always a SUNDAY. 2026-08-09 is
// one; stepping back 14 days gives Sunday 2026-07-26, whose period started Monday
// 2026-07-13. A one-day error here is invisible on screen — the periods stay
// fortnightly and simply run on the wrong days — so keep it in step with
// DEFAULT_PAYFLOW_RULES in askSmart-backend/src/utils/payflow/rules.ts.
const DEFAULT_PAY_PERIOD: PayPeriodConfig = { anchorEnd: "2026-08-09", lengthDays: 14 };

// Decision state is keyed per shift. An employee's 2nd+ shift on the same day
// carries a seq, which keeps those rows distinct; single-shift rows keep the
// original key shape.
export const rowKey = (
  ppId: string,
  dayIndex: number,
  empId: string,
  seq?: number,
): string => `${ppId}-${dayIndex}-${empId}${seq === undefined ? "" : `-${seq}`}`;

/* ── Formatting + comparison helpers ──────────────────────────────────── */

// What a figure the data does not have reads as on screen. An empty cell looks like a
// rendering fault; this says the value is missing on purpose.
const NO_VALUE = "—";

// The two sides of a shift, each undefined when the source had nothing for it. Every
// reader goes through these, so a missing side cannot decay into a zero, a copy of the
// other side, or a NaN on the way to the screen. Both boundaries of a pair are checked
// though the assembler writes them together — half a schedule is not a schedule.
export const scheduleSpan = (
  s: Pick<EmployeeShift, "schedStart" | "schedEnd">,
): { start: number; end: number } | undefined =>
  s.schedStart === undefined || s.schedEnd === undefined
    ? undefined
    : { start: s.schedStart, end: s.schedEnd };

export const workedSpan = (
  s: Pick<EmployeeShift, "actualIn" | "actualOut">,
): { in: number; out: number } | undefined =>
  s.actualIn === undefined || s.actualOut === undefined
    ? undefined
    : { in: s.actualIn, out: s.actualOut };

// Times are stored as minutes from the shift day's midnight, so an overnight
// clock-out can exceed 24:00 (e.g. 11:30 PM + 2h = 1530 min = 1:30 AM next
// day). Wrap into a 24-hour clock for display and flag the day rollover with
// a "(+1)" suffix so 1:30 AM isn't mistaken for 1:30 PM. Hour math elsewhere
// keeps using the raw minute values, so durations stay correct.
export const fmtTime = (mins: number): string => {
  const dayOffset = Math.floor(mins / 1440);
  const norm = ((mins % 1440) + 1440) % 1440;
  const h = Math.floor(norm / 60);
  const m = norm % 60;
  const ampm = h >= 12 ? "PM" : "AM";
  const hh = h % 12 === 0 ? 12 : h % 12;
  const suffix = dayOffset > 0 ? ` (+${dayOffset})` : "";
  return `${hh}:${m.toString().padStart(2, "0")} ${ampm}${suffix}`;
};

// A time the source never gave us — the scheduled start/end of a shift with no
// schedule. Blank, not a made-up clock reading.
export const fmtTimeOrNone = (mins: number | undefined): string =>
  mins === undefined ? NO_VALUE : fmtTime(mins);

// Minutes ⇄ "HH:MM" 24-hour value for <input type="time">. Wraps past
// midnight (1530 → "01:30") so an overnight clock-out edits cleanly.
export const minsToInputValue = (mins: number): string => {
  const norm = ((mins % 1440) + 1440) % 1440;
  const h = Math.floor(norm / 60);
  const m = norm % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

export const inputValueToMins = (val: string): number => {
  const [h, m] = val.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return 0;
  return h * 60 + m;
};

// A manager-confirmed clock-out, normalised against the confirmed clock-in. Out BEFORE
// in means the shift ran past midnight, so it belongs to the next day. Out EQUAL to in
// means no hours worked — how a manager records a scheduled shift that was not worked at
// all — and must stay a zero rather than rolling forward into a 24-hour shift.
export const confirmedOutMinutes = (inMins: number, outMins: number): number =>
  outMins < inMins ? outMins + 1440 : outMins;

// Suggested worked times used to pre-fill the manager's confirmation boxes:
//   • the extraordinary (> tolerance) side keeps the raw actual so the manager
//     can judge it (e.g. clocked in 90 min early)
//   • a within-tolerance red overage (in early / out late ≤ tolerance) snaps
//     back to the scheduled boundary — the normal auto-deduction still applies
//   • green time (in late / out early) is left at actual — never padded up
//   • an unscheduled shift has no boundary to snap to, so the clock times stand
//   • a shift with no clock record starts from the SCHEDULE — the only evidence of what
//     was meant to be worked (a forgotten clock-in is the usual cause). The manager
//     still has to confirm it, and sets In = Out to record that nothing was worked.
export const suggestedWorkedTimes = (s: {
  schedStart?: number;
  schedEnd?: number;
  actualIn?: number;
  actualOut?: number;
}): { in: number; out: number } => {
  const scheduled = scheduleSpan(s);
  const worked = workedSpan(s);
  if (!worked) return scheduled ? { in: scheduled.start, out: scheduled.end } : { in: 0, out: 0 };
  const effectiveOut = Math.min(worked.out, CLOSING_CAP_MINUTES); // 2:30 AM cap
  if (!scheduled) return { in: worked.in, out: effectiveOut };
  const earlyIn = Math.max(0, scheduled.start - worked.in);
  const lateOut = Math.max(0, effectiveOut - scheduled.end);
  const inException = earlyIn > TOLERANCE_MINUTES;
  const outException = lateOut > TOLERANCE_MINUTES;
  return {
    in: inException ? worked.in : earlyIn > 0 ? scheduled.start : worked.in,
    out: outException ? effectiveOut : lateOut > 0 ? scheduled.end : effectiveOut,
  };
};

export const hours = (start: number, end: number): number => (end - start) / 60;
export const fmtHours = (h: number): string => h.toFixed(2);
export const fmtMoney = (n: number): string =>
  `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// The roster/POS roles available to the org (source for the FOH-roles picker),
// derived from the shifts of every payroll whose detail has been loaded.
// A party/group booking is rung onto a dummy account named "Event 1", "Event 2", … .
// These are NOT real employees: their sales are a party's pooled money (all auto-grat),
// and they never get paid, scheduled, or shown as staff. Everything keys off the NAME.
const EVENT_POOL_ACCOUNT = /^\s*event\s*(\d+)\b/i;

export const isEventAccount = (name: string): boolean => EVENT_POOL_ACCOUNT.test(name);

// The canonical account label ("Event 2"), collapsing casing/spacing/suffix so the same
// party's rows group together. Null for a normal employee.
export const eventAccountName = (name: string): string | null => {
  const m = name.match(EVENT_POOL_ACCOUNT);
  return m ? `Event ${m[1]}` : null;
};

// A generic sales account for takeout orders. Takeout doesn't tip, so it's rung
// on its own account to keep staff from tipping out on it — which means it is
// never a real employee, never joins a pool, and never gets paid. Unlike an
// Event party it has no distribution at all: it's shown nowhere. Keyed off the
// NAME, tolerant of "TAKEOUT", "Take Out", "Take-Out".
const TAKEOUT_ACCOUNT = /^\s*take[\s-]?out\b/i;

export const isTakeoutAccount = (name: string): boolean => TAKEOUT_ACCOUNT.test(name);

// Accounts that are not real staff and must be kept off the staff-facing screens
// and out of the pools: the Event party accounts AND the Takeout account. Use
// this wherever a loop skips non-staff rows. Use isEventAccount / eventAccountName
// only for the party display and pool task, which Takeout has no part in.
export const isHiddenAccount = (name: string): boolean =>
  isEventAccount(name) || isTakeoutAccount(name);

export const rolesFromPeriods = (periods: PayPeriod[]): string[] => {
  const set = new Set<string>();
  for (const p of periods)
    for (const d of p.days)
      for (const r of d.rows) if (!isHiddenAccount(r.name)) set.add(r.role);
  return [...set].sort((a, b) => a.localeCompare(b));
};

// Distinct employees (id + name) across every loaded payroll — the source for the
// Salaried Employees picker. First name seen for an id wins.
export const employeesFromPeriods = (
  periods: PayPeriod[],
): { id: string; name: string }[] => {
  const byId = new Map<string, string>();
  for (const p of periods)
    for (const d of p.days)
      for (const r of d.rows)
        if (!isHiddenAccount(r.name) && !byId.has(r.id)) byId.set(r.id, r.name);
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

/* ── Tip-outs ─────────────────────────────────────────────────────────────
 * Rows carry real POS sales when the pipeline found a sales record. Rows
 * without one have no sales and owe nothing. The pooled-out columns (Tip Pool,
 * CC Fee, Host, BarBack) are computed on the admin screen.
 */
export interface Tipout {
  hasSales: boolean;
  netSales: number;
  cash: number; // cash tips (kept in hand)
  ccTips: number; // credit-card tips (owed)
  autoGrat: number; // automatic gratuity (owed)
  tipsOwed: number; // tips portion owed via payroll
  autoOwed: number; // auto-grat portion owed
  totalOwed: number;
}

/* ── Tip pools (auto-grouped by role + day/night) ─────────────────────────
 * Bartenders pool by day/night (5 PM cut-off, even split); barbacks and
 * hosts/food-runners each pool by hours. Servers aren't pooled (yet).
 */
export type PoolKey =
  | "bar-day"
  | "bar-night"
  | "barback-day"
  | "barback-night"
  | "host-day"
  | "host-night";

// Display order for tasks: bartenders, then host/runner, then barback.
export const POOL_ORDER: PoolKey[] = [
  "bar-day",
  "bar-night",
  "host-day",
  "host-night",
  "barback-day",
  "barback-night",
];

export interface PoolMeta {
  label: string;
  color: string; // border / accent
  tint: string; // faint row background
}

export const POOL_META: Record<PoolKey, PoolMeta> = {
  "bar-day": { label: "Bartender Pool · Day", color: "#3b82f6", tint: "rgba(59,130,246,0.12)" },
  "bar-night": { label: "Bartender Pool · Night", color: "#a855f7", tint: "rgba(168,85,247,0.12)" },
  "host-day": { label: "Host / Runner Pool · Day", color: "#d4a017", tint: "rgba(212,160,23,0.12)" },
  "host-night": { label: "Host / Runner Pool · Night", color: "#e0852b", tint: "rgba(224,133,43,0.12)" },
  "barback-day": { label: "BarBack Pool · Day", color: "#14b8a6", tint: "rgba(20,184,166,0.12)" },
  "barback-night": { label: "BarBack Pool · Night", color: "#0e7490", tint: "rgba(14,116,144,0.14)" },
};

// The boundaries a shift is pooled by: its schedule when there is one, else the hours it
// actually worked. An unscheduled shift still belongs to a bar and a window and still
// earned tips — reading its absent schedStart as 0 would file every one of them in the
// day pool regardless of when it was worked. Undefined only for a row with neither side.
export const poolWindowSpan = (
  s: EmployeeShift,
): { start: number; end: number } | undefined => {
  const scheduled = scheduleSpan(s);
  if (scheduled) return scheduled;
  const worked = workedSpan(s);
  return worked && { start: worked.in, end: worked.out };
};

// Shift window by start minute (before/after 5 PM) — see poolWindowSpan for which
// start that is.
export const shiftWindow = (schedStart: number): "day" | "night" =>
  schedStart < 17 * 60 ? "day" : "night";

// Which pool a role belongs to in a given window (null = not pooled:
// server/kitchen/closer). The window is decided by the caller — by start time
// (shiftWindow) or by where most of the shift's hours fall — so this stays the
// single source for the role → pool mapping either way.
export const poolKeyForWindow = (role: string, w: "day" | "night"): PoolKey | null => {
  if (role === "Bartender") return `bar-${w}` as PoolKey;
  if (/barback/i.test(role)) return `barback-${w}` as PoolKey;
  if (/host|food.?runner/i.test(role)) return `host-${w}` as PoolKey;
  return null;
};

// Which pool a shift belongs to, keyed by its start minute.
export const poolKeyForShift = (role: string, schedStart: number): PoolKey | null =>
  poolKeyForWindow(role, shiftWindow(schedStart));

// Key for the per-day pool membership an admin can edit.
export const poolDayKey = (periodId: string, dayIndex: number): string =>
  `${periodId}:${dayIndex}`;

// Sales for a row the pipeline found no sales record for.
export const EMPTY_SHIFT_SALES: Readonly<ShiftSales> = Object.freeze({
  netSales: 0,
  cash: 0,
  ccTips: 0,
  autoGrats: 0,
});

// Tip-out from the row's real POS figures.
export function tipoutFromSales(sales: ShiftSales): Tipout {
  const tipsOwed = sales.ccTips;
  const autoOwed = sales.autoGrats;
  return {
    hasSales: sales.netSales > 0 || sales.ccTips > 0 || sales.autoGrats > 0,
    netSales: sales.netSales,
    cash: sales.cash,
    ccTips: sales.ccTips,
    autoGrat: sales.autoGrats,
    tipsOwed,
    autoOwed,
    totalOwed: Math.round((tipsOwed + autoOwed) * 100) / 100,
  };
}

export const fmtSigned = (h: number): string => `${h > 0 ? "+" : ""}${h.toFixed(2)}`;

// Clock-IN colour: in AFTER schedule (late) → green; BEFORE (early) → red. An unmatched
// row gets no colour — there is nothing to be early or late against.
export const inClass = (actualIn?: number, schedStart?: number): string => {
  if (actualIn === undefined || schedStart === undefined) return "";
  return actualIn > schedStart ? "payflow-hl-green" : actualIn < schedStart ? "payflow-hl-red" : "";
};

// Clock-OUT colour: out BEFORE schedule (early) → green; AFTER (late) → red.
export const outClass = (actualOut?: number, schedEnd?: number): string => {
  if (actualOut === undefined || schedEnd === undefined) return "";
  return actualOut < schedEnd ? "payflow-hl-green" : actualOut > schedEnd ? "payflow-hl-red" : "";
};

// Hours-delta colour (Total Actual + Variance): under → green; over → red.
export const deltaClass = (delta: number): string =>
  delta < 0 ? "payflow-hl-green" : delta > 0 ? "payflow-hl-red" : "";

// Unpaid-break deduction for a worked shift: the deduction of the highest
// threshold the shift exceeds (rules are tiered, not cumulative — a 10.5h
// shift deducts 1h, not 0.5h + 1h).
export const breakDeductionFor = (workedHours: number, rules: BreakRule[]): number => {
  const matching = rules.filter((r) => workedHours > r.overHours);
  if (matching.length === 0) return 0;
  return matching.reduce((a, b) => (b.overHours > a.overHours ? b : a)).deductHours;
};

// Does a date (yyyy-mm-dd) fall on a committed stat holiday (any locked year)?
export const isStatHoliday = (dateISO: string, committed: CommittedHolidays): boolean =>
  Object.values(committed).some((list) => list.some((h) => h.date === dateISO));

/* ── Per-row computation (shared by both screens) ─────────────────────── */

export type RowStatus = "none" | "pending" | "confirmed";

// A manual hours adjustment entered by an admin. Always carries a reason.
export interface AdminAdjustment {
  hours: number; // signed adjustment vs scheduled
  reason: string;
}

export interface RowComputation {
  // Hours from the side of the match that exists; 0 from a side that does not, so an
  // unmatched row's variance is its whole shift — worked-but-unscheduled reads +8.25,
  // scheduled-but-never-worked reads −8.00. The TIMES stay absent (see EmployeeShift);
  // it is only the totals that count a missing side as zero hours.
  schedTotal: number;
  actualTotal: number;
  variance: number;
  earlyInMins: number; // red minutes clocked in before scheduled start
  lateOutMins: number; // red minutes clocked out after scheduled end
  inException: boolean; // earlyIn beyond tolerance
  outException: boolean; // lateOut beyond tolerance
  // The two unmatched shapes. Both are manager tasks in their own right: an unscheduled
  // shift has to be authorised after the fact, and a schedule with no clock record has to
  // be answered ("they worked it and forgot to clock in" / "they did not work"). Neither
  // can be judged by tolerance — there is no schedule to be late against, and no clock
  // record to be late with — so they are exceptions on their own terms.
  unscheduled: boolean; // worked, with no schedule behind it
  unworked: boolean; // scheduled, with no clock record
  hasException: boolean;
  pendingMins: number; // extraordinary minutes awaiting/under manager review (0 if unmatched)
  effectiveOut?: number; // clock-out after the closing cap + early-out deduction; absent if never clocked
  outCapped: boolean; // raw clock-out was pulled back to the 2:30 AM cap
  earlyOutDeducted: boolean; // early clock-out had a flat 15 min deducted
  breakDeduct: number; // unpaid-break hours removed from net
  status: RowStatus; // exception status (manager view): none | pending | confirmed
  isManual: boolean; // an admin manual adjustment is in effect
  reason: string; // manager (confirmed) or admin (manual) reason
  confirmedIn?: number; // manager-confirmed worked times (when status === confirmed)
  confirmedOut?: number;
  confirmedBy?: string; // who confirmed it
  confirmedAt?: string; // ISO timestamp of when it was confirmed
  adjustNum: number; // Net − Scheduled, i.e. signed deviation from the schedule
  adjustValue: string; // signed display string for the Adjust column, e.g. "+1.00"
  netHours: number;
}

export function computeRow(
  r: EmployeeShift,
  adjustment: AdminAdjustment | undefined,
  review: Review | undefined,
  breakRules: BreakRule[] = [],
): RowComputation {
  // Each side of the match, present or not. A side that is missing contributes no times
  // and no hours; it is never stood in for by the side that is there.
  const scheduled = scheduleSpan(r);
  const worked = workedSpan(r);
  const schedTotal = scheduled ? hours(scheduled.start, scheduled.end) : 0;

  // Apply the 2:30 AM closing cap before anything else: a clock-out past the
  // cap is pulled back to it, silently (never triggers a manager review). A shift with
  // no clock record has no clock-out to cap, so it has none to show either.
  const cappedOut = worked ? Math.min(worked.out, CLOSING_CAP_MINUTES) : undefined;
  const outCapped = worked !== undefined && worked.out > CLOSING_CAP_MINUTES;

  // Early departure: clocking out before the scheduled end deducts a flat 15
  // minutes from the clock-out (e.g. 4:51 → 4:36). On-time or late clock-outs are
  // untouched, and it never drops below the clock-in.
  const effectiveOut =
    cappedOut !== undefined && scheduled && cappedOut < scheduled.end
      ? Math.max(worked!.in, cappedOut - 15)
      : cappedOut;
  const earlyOutDeducted = effectiveOut !== undefined && effectiveOut !== cappedOut;

  const actualTotal = worked && effectiveOut !== undefined ? hours(worked.in, effectiveOut) : 0;
  const variance = actualTotal - schedTotal;

  // Early-in and late-out are comparisons: they need both sides.
  const earlyInMins = scheduled && worked ? Math.max(0, scheduled.start - worked.in) : 0;
  const lateOutMins =
    scheduled && effectiveOut !== undefined ? Math.max(0, effectiveOut - scheduled.end) : 0;

  const inException = earlyInMins > TOLERANCE_MINUTES;
  const outException = lateOutMins > TOLERANCE_MINUTES;
  const unscheduled = worked !== undefined && scheduled === undefined;
  const unworked = scheduled !== undefined && worked === undefined;
  // A row with neither side is a sales-only row the pipeline could not match to a shift
  // (the build warns about it). There are no hours for a manager to rule on, so it is
  // not a task — it stays at zero hours and carries its money.
  const hasException = inException || outException || unscheduled || unworked;
  const pendingMins = (inException ? earlyInMins : 0) + (outException ? lateOutMins : 0);

  const status: RowStatus = hasException ? (review ? "confirmed" : "pending") : "none";

  // The worked-hours basis before any admin override:
  //   none      → actual, minus any within-tolerance red overage (in early / out late)
  //   confirmed → the manager-confirmed worked hours
  //   pending   → held until the manager confirms
  let autoWorked = actualTotal;
  if (status === "none") {
    autoWorked = actualTotal - (earlyInMins + lateOutMins) / 60;
  } else if (status === "confirmed" && review) {
    autoWorked = hours(review.confirmedIn, review.confirmedOut);
  }

  // Admin manual override wins (only offered on non-pending rows). Otherwise
  // the Adjust column is the signed deviation of net worked hours from schedule.
  const isManual = adjustment !== undefined && status !== "pending";
  const isHeld = status === "pending" && !isManual;

  let netHours: number;
  let adjustNum: number;
  if (isHeld) {
    netHours = NaN; // shown as "—" until confirmed
    adjustNum = 0;
  } else if (isManual && adjustment) {
    adjustNum = adjustment.hours;
    netHours = schedTotal + adjustNum;
  } else {
    netHours = autoWorked;
    adjustNum = netHours - schedTotal;
  }

  // Unpaid-break deduction applies to the (post-adjust) worked hours. Held
  // rows have no net yet, so no deduction.
  const breakDeduct = isHeld ? 0 : breakDeductionFor(netHours, breakRules);
  if (!isHeld) netHours -= breakDeduct;

  const reason = isManual && adjustment ? adjustment.reason : review ? review.reason : "";
  const adjustValue = isHeld ? "" : fmtSigned(adjustNum);

  return {
    schedTotal, actualTotal, variance,
    earlyInMins, lateOutMins,
    inException, outException, unscheduled, unworked, hasException,
    pendingMins, effectiveOut, outCapped, earlyOutDeducted, breakDeduct,
    status, isManual, reason,
    confirmedIn: review?.confirmedIn,
    confirmedOut: review?.confirmedOut,
    confirmedBy: review?.confirmedBy,
    confirmedAt: review?.confirmedAt,
    adjustNum, adjustValue, netHours,
  };
}

// What a row needs a manager for, in words. One definition for both screens — the admin
// sheet's on-hold tooltip and the manager queue's task line have to name the same thing.
export const exceptionSummary = (c: {
  unscheduled: boolean;
  unworked: boolean;
  inException: boolean;
  outException: boolean;
  pendingMins: number;
}): string => {
  if (c.unscheduled) return "worked with no scheduled shift";
  if (c.unworked) return "scheduled with no clock record";
  return `${c.pendingMins} min over schedule`;
};

// The same, as the short label the manager queue puts on a task row.
export const exceptionLabel = (c: {
  unscheduled: boolean;
  unworked: boolean;
  inException: boolean;
  outException: boolean;
}): string => {
  if (c.unscheduled) return "Unscheduled Shift";
  if (c.unworked) return "No Clock Record";
  if (c.inException && c.outException) return "ClockIn & ClockOut Issue";
  return c.inException ? "ClockIn Issue" : "ClockOut Issue";
};

// Are there any unresolved (pending) manager exceptions in a period?
export function periodHasPending(
  period: PayPeriod,
  adjustments: Record<string, AdminAdjustment>,
  reviews: Record<string, Review>,
): boolean {
  for (const day of period.days) {
    for (const r of day.rows) {
      // Event party buckets and the Takeout account are never a real clock-in/out
      // to reconcile, so they raise no exception.
      if (isHiddenAccount(r.name)) continue;
      const key = rowKey(period.id, day.index, r.id, r.seq);
      if (computeRow(r, adjustments[key], reviews[key]).status === "pending") return true;
    }
  }
  return false;
}

// Does the period have any Event-account party at all? (Any → the tips stage has manager
// pool tasks to resolve.)
export function periodNeedsManualPool(period: PayPeriod): boolean {
  return partiesInPeriod(period).length > 0;
}

// The manual-pool step is settled when every Event-account party in the period has had its
// pool built and confirmed — or there are no parties at all. Until the day detail has
// loaded we cannot enumerate the parties, so it stays unsettled during that brief window
// rather than flashing a period to "approved".
export function manualPoolSettled(
  period: PayPeriod,
  manualPools: Record<string, ManualPoolState>,
): boolean {
  if (!period.daysLoaded) return false;
  const parties = partiesInPeriod(period);
  if (parties.length === 0) return true;
  const state = manualPools[period.id];
  return parties.every((p) => !!state?.parties?.[p.key]?.resolved);
}

// The bar-allocation step is settled when every server-to-bar issue in the period (money
// charged with no Main Bar pool to receive it) has a manager allocation — or there are no
// such issues. Unsettled until the day detail has loaded, like manualPoolSettled.
export function barAllocationSettled(
  period: PayPeriod,
  barAllocations: Record<string, Record<string, BarAllocation>>,
): boolean {
  if (!period.daysLoaded) return false;
  const issues = barIssuesInPeriod(period);
  if (issues.length === 0) return true;
  const resolved = barAllocations[period.id] ?? {};
  return issues.every((i) => !!resolved[i.key]);
}

// Any outstanding manager task for the period — unresolved clock-in/out
// exceptions OR an unsettled manual-pool task OR unallocated server-to-bar money. Blocks
// Verify Tips.
export function periodHasOutstandingTasks(
  period: PayPeriod,
  adjustments: Record<string, AdminAdjustment>,
  reviews: Record<string, Review>,
  manualPools: Record<string, ManualPoolState>,
  barAllocations: Record<string, Record<string, BarAllocation>>,
): boolean {
  return (
    periodHasPending(period, adjustments, reviews) ||
    !manualPoolSettled(period, manualPools) ||
    !barAllocationSettled(period, barAllocations)
  );
}

// Roll a period up to the status that tells the admin who works next:
//   pending      → waiting on manager tasks (clock-in/out at the hours stage,
//                  pool tasks at the tips stage) — shown yellow
//   ready        → hours stage clear, admin can Verify Hours (blue)
//   approved     → hours verified + pools done, admin can Verify Tips (blue)
//   tipsverified → tips verified, admin can Process (blue)
//   processed    → payroll sent (green)
export function periodStatus(
  period: PayPeriod,
  adjustments: Record<string, AdminAdjustment>,
  reviews: Record<string, Review>,
  hoursApproved: Record<string, HoursApproval>,
  tipsApproved: Record<string, HoursApproval> = {},
  processedPeriods: Record<string, HoursApproval> = {},
  manualPools: Record<string, ManualPoolState> = {},
  barAllocations: Record<string, Record<string, BarAllocation>> = {},
): PayPeriodStatus {
  const id = period.id;
  if (period.stage === "processed" || processedPeriods[id]) return "processed";
  if (tipsApproved[id]) return "tipsverified";
  if (hoursApproved[id]) {
    // Tips stage: pending until the manager tasks are settled — the manual pool answered
    // (or no Event sales), and any unallocated server-to-bar money allocated.
    return manualPoolSettled(period, manualPools) && barAllocationSettled(period, barAllocations)
      ? "approved"
      : "pending";
  }
  // Hours stage: pending until the clock-in/out exceptions are resolved.
  return periodHasPending(period, adjustments, reviews) ? "pending" : "ready";
}

/* ── Store ────────────────────────────────────────────────────────────── */

// Admin sign-off that a period's hours are final (locks Net Hours).
export interface HoursApproval {
  by: string;
  at: string;
}

// Per-employee-per-role overrides on the Summary report. Keyed
// `${periodId}:${empId}:${role}`. Unset fields fall back to the flat defaults.
export interface SummaryOverride {
  rate?: number; // hourly rate override ($/hr)
  tipPool?: number; // tip-pool override ($/hr)
  fohOff?: boolean; // FOH premium turned off for this line
}

// Manual (ad-hoc) tip pool — e.g. a group-buyout booking. The manager creates
// one or more named groups of staff; until the manual-pool task is resolved the
// period's tips stage stays pending.
export interface ManualPoolGroup {
  id: string;
  name: string;
  memberIds: string[];
  // Share of this party's pool the group receives, as a whole-number percent. The groups
  // in a party are expected to total 100; the slice then splits evenly among its members.
  pct: number;
}
// One party's pool: the manager's allocation of a single Event-account booking. Keyed in
// ManualPoolState by `${dateISO}|Event N` (see partyKey), so Event 1 and Event 2, or the
// same account on two different days, are independent pools with independent tasks.
export interface PartyPool {
  resolved: boolean; // the manager has built and confirmed this party's allocation
  by?: string;
  at?: string;
  groups: ManualPoolGroup[];
}
export interface ManualPoolState {
  parties: Record<string, PartyPool>;
}

// Stable id for a party = its Event account on a given day.
export const partyKey = (dateISO: string, eventName: string): string =>
  `${dateISO}|${eventName}`;

// Every Event-account party in a period that carries money to distribute, with its pool
// total (all auto-grat) — one per (day, Event account). The source for the manager's
// per-party tasks and for the tip distribution.
export interface PartySummary {
  key: string;
  dateISO: string;
  dayIndex: number;
  weekday: string;
  dateLabel: string;
  eventName: string; // "Event 2"
  // The pool the manager distributes: (CC tips − tip pool − CC fee) + auto-grats. Party
  // tipouts are pool + CC only (no host/barback). Computed under the period's snapshot
  // tip rates so it matches what the admin Tips tab splits.
  pool: number;
}

export function partiesInPeriod(period: PayPeriod): PartySummary[] {
  const rates = period.rules?.tipRates;
  const poolPct = rates?.tipPool ?? 0;
  const ccPct = rates?.creditCardFee ?? 0;
  // Accumulate the raw sales components per party first, then derive the pool once.
  const acc = new Map<
    string,
    Omit<PartySummary, "pool"> & { netSales: number; ccTips: number; autoGrats: number }
  >();
  for (const day of period.days) {
    for (const r of day.rows) {
      const eventName = eventAccountName(r.name);
      if (!eventName || !r.sales) continue;
      const key = partyKey(day.dateISO, eventName);
      const cur =
        acc.get(key) ??
        {
          key,
          dateISO: day.dateISO,
          dayIndex: day.index,
          weekday: day.weekday,
          dateLabel: day.dateLabel,
          eventName,
          netSales: 0,
          ccTips: 0,
          autoGrats: 0,
        };
      cur.netSales += r.sales.netSales;
      cur.ccTips += r.sales.ccTips;
      cur.autoGrats += r.sales.autoGrats;
      acc.set(key, cur);
    }
  }
  return [...acc.values()]
    .map((a) => {
      // Party payments come in on credit card, so the CC fee applies to the whole tip take
      // (CC tips + auto-grats); pool is a % of net sales. Deductions are pool + CC only.
      const partyTips = a.ccTips + a.autoGrats;
      const pool = partyTips - (a.netSales * poolPct) / 100 - (partyTips * ccPct) / 100;
      const { netSales: _netSales, ccTips: _ccTips, autoGrats: _autoGrats, ...rest } = a;
      return { ...rest, pool };
    })
    // A party only surfaces when there is something to split.
    .filter((p) => p.pool > 0)
    .sort((a, b) => a.dateISO.localeCompare(b.dateISO) || a.eventName.localeCompare(b.eventName));
}

// A manager's allocation of server-to-bar money for a window that had no bartender on Main
// Bar. `recipients` = the employee ids to split it among (bartenders/managers on duty that
// shift); an EMPTY list means nobody — the servers pay no tip-out for that window.
export interface BarAllocation {
  recipients: string[];
  by: string;
  at: string;
}

// A candidate recipient the manager can pick from — a bartender or manager on duty that day.
export interface BarCandidate {
  id: string;
  name: string;
  role: string;
}

export interface BarAllocationIssue {
  key: string; // `${dateISO}|${window}`
  periodId: string;
  dateISO: string;
  dayIndex: number;
  weekday: string;
  dateLabel: string;
  window: "day" | "night";
  amount: number; // server-to-bar money charged with no Main Bar bartender to receive it
  candidates: BarCandidate[]; // bartenders/managers on duty that day — the manager picks from these
}

// Server-to-bar money charged in a window where NO bartender was allocated to Main Bar, per
// day+window. All servers' bar tip-out goes to the Main Bar pool; when there is no Main Bar
// bartender to receive it (every bartender is at Parlour/Patio, say), a manager must say who
// gets it. Mirrors ManagePayFlow.computeDayTips: servers pay serverToBar% of net sales
// whenever a bartender is present in their window, and Main Bar bartenders receive it by
// allocation (role/location, not their own sales). KEEP IN SYNC with that block.
export function barIssuesInPeriod(period: PayPeriod): BarAllocationIssue[] {
  const serverToBarPct = period.rules?.tipRates?.serverToBar ?? 0;
  if (serverToBarPct <= 0) return [];
  const POOL_BOUNDARY = 17 * 60; // 5 PM, minutes from midnight
  const baseRoleOf = (role: string) => role.replace(/\s*\(SV\)\s*$/i, "");
  // A server keeps its role even with a bar LOCATION assigned (Parlour/Patio) — only a
  // Bartender, or a non-server given a bar location (e.g. a manager on the bar), is bar-like.
  const isBarLike = (r: { role: string; location?: string }) =>
    baseRoleOf(r.role) === "Bartender" || (!!r.location && baseRoleOf(r.role) !== "Server");
  const isManagerRole = (role: string) => /manager|\bmod\b/i.test(role);
  // No location, or "Main Bar", counts as Main Bar; Parlour/Patio etc. do not.
  const onMainBar = (r: { location?: string }) => (r.location || "Main Bar") === "Main Bar";
  const windowOf = (s: EmployeeShift): "day" | "night" => {
    const span = poolWindowSpan(s) ?? { start: 0, end: 0 };
    if (span.end <= span.start) return "day";
    const dayMins = Math.max(0, Math.min(span.end, POOL_BOUNDARY) - span.start);
    const nightMins = Math.max(0, span.end - Math.max(span.start, POOL_BOUNDARY));
    return nightMins >= dayMins ? "night" : "day";
  };
  const issues: BarAllocationIssue[] = [];
  for (const day of period.days) {
    const classified = day.rows
      .filter((r) => !isHiddenAccount(r.name))
      .map((r) => ({ r, win: windowOf(r) }));
    // Bartenders/managers on duty that DAY — the pool of people a manager can allocate to.
    const candidates: BarCandidate[] = [];
    const seen = new Set<string>();
    for (const { r } of classified) {
      if (!(isBarLike(r) || isManagerRole(r.role))) continue;
      const cid = r.id.split("~")[0];
      if (seen.has(cid)) continue;
      seen.add(cid);
      candidates.push({ id: cid, name: r.name, role: r.role });
    }
    for (const win of ["day", "night"] as const) {
      const inWin = classified.filter((x) => x.win === win);
      // Servers are charged only when a bartender is present in the window.
      if (!inWin.some((x) => isBarLike(x.r))) continue;
      const amount = inWin
        .filter((x) => baseRoleOf(x.r.role) === "Server" && x.r.sales)
        .reduce((s, x) => s + (x.r.sales!.netSales * serverToBarPct) / 100, 0);
      if (amount <= 0) continue; // no server-to-bar charged this window
      // A bartender allocated to Main Bar receives it (regardless of their own sales).
      if (inWin.some((x) => isBarLike(x.r) && onMainBar(x.r))) continue;
      issues.push({
        key: `${day.dateISO}|${win}`,
        periodId: period.id,
        dateISO: day.dateISO,
        dayIndex: day.index,
        weekday: day.weekday,
        dateLabel: day.dateLabel,
        window: win,
        amount,
        candidates,
      });
    }
  }
  return issues;
}

// One staff member's on-account total parsed from the uploaded report.
export interface StaffAccountEntry {
  name: string;
  amount: number;
}

// The Staff Accounts report is uploaded once per pay period (not API-fed).
// We record its metadata + the parsed per-staff on-account totals against the
// period; the file itself lives in blob storage in production.
export interface StaffAccountsReport {
  fileName: string;
  uploadedBy: string;
  uploadedAt: string;
  entries: StaffAccountEntry[];
  // Admin verifies the ingested amounts before they lock; until then the
  // amounts are editable on the Overview tab.
  verified?: boolean;
  verifiedBy?: string;
  verifiedAt?: string;
  // The admin declared there was no staff-account activity this period, instead of
  // uploading a report. Recorded as an empty, already-verified report so the flow can
  // proceed and the "report not uploaded" warning does not fire — the flag only changes
  // how it reads on screen (no file was skipped, none was expected).
  skipped?: boolean;
}

// Something that went wrong with a data pull, and whether it clears itself.
//
// The two causes need opposite lifetimes, which is why this is not a bare string. A
// failure to START is the result of an action someone took: it has to stay on screen
// until they acknowledge it or try again, or it flashes past between two poll
// responses and they never learn their pull did not begin. A failure to READ is this
// tab losing contact with the server; it is stale the moment contact returns, and
// leaving it up would accuse a healthy pull of being broken.
export interface DataPullProblem {
  message: string;
  transient: boolean; // true → the next successful read clears it
}

// What THIS tab knows about a pull it started, which is not the same thing as what the
// server reports and must not be inferred from it.
//
// The pipeline hands back a runId and guarantees the run doc exists before it answers
// (see new_run_doc in variance-etl-pipeline/app/runs.py). So between starting a run and
// seeing it, "the server reports no run" is not evidence that no run is happening — it
// is evidence that we cannot see ours. Those were the same state here, which is how a
// pull that was genuinely running presented as one that had never been asked for: the
// modal read `state: 'idle'`, concluded nothing was in flight, and closed itself.
//
//   starting    POST /runs is in flight; there is no runId yet
//   confirming  the 202 came back; the run doc has not shown us this runId yet
//   watching    the server has confirmed this runId at least once
//
// `blindSince` is orthogonal to the phase: it records when the SERVER last started
// failing to read ReconState, which can happen from either phase and says nothing about
// where the run has got to. Null whenever the last read was conclusive. It exists so
// "we cannot see anything" is bounded on its own clock rather than riding the run's —
// see RUN_BLIND_LIMIT_MS.
export type PullWatch =
  | { phase: "starting" }
  | { phase: "confirming"; runId: string; since: number; blindSince: number | null }
  | { phase: "watching"; runId: string; blindSince: number | null };

// A period's frozen stage outputs: what each verified tab showed when it was verified.
// Rendered in place of a recomputation once the matching stage is locked, so a verified
// tab cannot change under a later engine edit. A stage absent here is not frozen (or
// predates freezing), and that tab falls back to the live computation.
export interface FrozenStages {
  hours?: Record<string, RowComputation>; // rowKey (sans period prefix) → frozen Hours row
  tips?: unknown; // frozen at Verify Tips (wired in a later step)
  overview?: unknown; // frozen at Verify Overview (wired in a later step)
}

interface PayflowState {
  payPeriods: PayPeriod[];
  hydrated: boolean; // the first server load has settled (either way)
  syncError: string | null; // last server failure, for the admin banner
  // Set when a build was refused because the period's dates aren't in PayFlow's
  // data yet ("not-covered"). Carries the end date of the period that was
  // attempted, so the banner can offer to pull exactly that fortnight and, once
  // it lands, build the payroll for it. Null unless the last build hit that wall.
  coverageGap: { endISO: string } | null;
  // The pay period whose payroll is being created right now, or null. A build is a
  // single request, so there is no progress to carry — only whether one is in flight.
  building: string | null;
  // The current (or most recent) data pull, straight from the server. Not persisted
  // and not derived: a pull is org-wide and can be started by another admin or by the
  // nightly schedule, so the server's view is the only correct one and this tab just
  // mirrors it.
  dataPull: DataPullStatus | null;
  dataPullError: DataPullProblem | null; // last failure to start or read a pull
  // The pull THIS tab started, tracked by run id from the moment the button is pressed
  // until that run is confirmed finished. Null when this tab has not started one — it
  // may still be watching someone else's, which is `dataPull` above.
  dataPullWatch: PullWatch | null;
  // Which dates already have data stored, keyed by ISO date. Loaded per visible month
  // while the pull calendar is open, so months nobody looks at cost nothing.
  dateCoverage: Record<string, DateCoverage>;
  // Month keys ("YYYY-MM") already asked for, successfully or not. Stops an open
  // calendar re-requesting the same month on every render, and is what lets "no entry
  // for this date" mean "not loaded yet" rather than "no data" — the two must not draw
  // the same, or an unloaded month reads as a month with nothing in it.
  dateCoverageLoaded: string[];
  // Whether each payroll's source records have changed since it was built, keyed by
  // payroll id. Server-derived and never persisted: it is a fact about stored data at
  // a moment, and a cached copy from a previous session would be a claim we cannot
  // stand behind. Absent until asked for; nothing changes a payroll on the strength
  // of it.
  drift: Record<string, DriftReport>;
  // The concurrency token for each payroll's decisions, as last seen from the server.
  // Sent back when locking or reopening a stage so two admins racing one cannot both
  // win. Null for a payroll nobody has decided anything about yet.
  periodVersion: Record<string, string | null>;
  /* The decision maps below are SERVER state, mirrored here.
     They keep their original global shape — keyed `${periodId}-…` / `${periodId}:…`
     across every payroll — because that is what the screens read. The server stores
     them per payroll without the prefix; `mergePeriodState` adds it on the way in and
     the actions strip it on the way out. That boundary is the entire reason the
     payroll screens and their arithmetic did not have to change. */
  adjustments: Record<string, AdminAdjustment>; // admin manual adjustments, by rowKey
  reviews: Record<string, Review>; // manager decisions, by rowKey
  committedHolidays: CommittedHolidays; // year → locked holiday dates
  holidayDrafts: Record<string, string>; // `${year}:${key}` → admin-picked ISO
  breakRules: BreakRule[];
  breakRulesLock: TipRatesLock | null; // non-null when committed/locked
  tipRates: TipRates;
  tipRatesLock: TipRatesLock | null; // non-null when committed/locked
  fohRoles: string[]; // roles eligible for the FOH premium
  fohRolesLock: TipRatesLock | null; // non-null when committed/locked
  salariedEmployeeIds: string[]; // employee ids paid a fixed salary (no hourly figures)
  salariedLock: TipRatesLock | null; // non-null when committed/locked
  payConstants: PayConstants; // flat pay figures used by the Summary report
  payPeriod: PayPeriodConfig; // anchor + length driving the selectable end dates
  hoursApproved: Record<string, HoursApproval>; // periodId → hours verified (locked)
  tipsApproved: Record<string, HoursApproval>; // periodId → tips verified (locked)
  poolSolo: Record<string, string[]>; // periodId → empIds removed from pooling (pool solo)
  processedPeriods: Record<string, HoursApproval>; // periodId → payroll processed
  summaryOverrides: Record<string, SummaryOverride>; // summary rate/tip-pool/FOH edits
  manualPools: Record<string, ManualPoolState>; // periodId → manual pool task/result
  // periodId → { `${dateISO}|${window}` → manager's allocation } for unreceived
  // server-to-bar money. An unresolved issue blocks Verify Tips.
  barAllocations: Record<string, Record<string, BarAllocation>>;
  staffAccounts: Record<string, StaffAccountsReport>; // periodId → uploaded report
  // periodId → the newest stored processed register (what was actually paid), or null
  // when the period has none. Absent (undefined) means "not fetched yet". The Summary
  // tab renders from this once a period is processed, so a finished payroll shows what
  // was paid rather than a fresh recomputation that a later engine change could move.
  registers: Record<string, StoredPayrollRegister | null>;
  // periodId → that period's frozen stage outputs (Hours / Tips / Overview). A verified
  // tab renders from here rather than recomputing. Absent until the period's state loads.
  frozenStages: Record<string, FrozenStages>;
  auditLog: AuditEntry[]; // newest first
}

// Bump the version suffix whenever the persisted shape changes so stale state
// from a previous build is discarded instead of silently rehydrating.
const STORAGE_KEY = "payflow.drafts.v1";

// What we persist, and it is now ONE thing: the holiday dates an admin is part-way
// through typing on the Rules tab, before they commit them.
//
// Everything else that used to live here — adjustments, manager confirmations, the
// verification locks, summary overrides, staff accounts, the audit log — is server
// state now, keyed to the payroll it belongs to and readable from any machine. A
// payroll record that only existed in one browser's localStorage was one cleared cache
// away from gone.
//
// Holiday drafts stay because they are not payroll state: they are a scratchpad, per
// person and per year, and are discarded the moment the year is committed.
interface PersistedState {
  holidayDrafts: Record<string, string>;
}

const hydrate = (parsed: Partial<PersistedState> | null | undefined): PayflowState => ({
  payPeriods: [],
  hydrated: false,
  syncError: null,
  coverageGap: null,
  building: null,
  dataPull: null,
  dataPullError: null,
  dataPullWatch: null,
  dateCoverage: {},
  dateCoverageLoaded: [],
  drift: {},
  periodVersion: {},
  adjustments: {},
  reviews: {},
  committedHolidays: {},
  holidayDrafts: parsed?.holidayDrafts || {},
  breakRules: DEFAULT_BREAK_RULES,
  breakRulesLock: null,
  tipRates: DEFAULT_TIP_RATES,
  tipRatesLock: null,
  fohRoles: DEFAULT_FOH_ROLES,
  fohRolesLock: null,
  salariedEmployeeIds: [],
  salariedLock: null,
  payConstants: DEFAULT_PAY_CONSTANTS,
  payPeriod: DEFAULT_PAY_PERIOD,
  hoursApproved: {},
  tipsApproved: {},
  poolSolo: {},
  processedPeriods: {},
  summaryOverrides: {},
  manualPools: {},
  barAllocations: {},
  staffAccounts: {},
  registers: {},
  frozenStages: {},
  auditLog: [],
});

const loadState = (): PayflowState => {
  if (typeof window === "undefined" || !window.localStorage) return hydrate(null);
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) return hydrate(JSON.parse(raw));
  } catch {
    /* fall through to a fresh default */
  }
  return hydrate(null);
};

let state: PayflowState = loadState();

const persist = () => {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    const payload: PersistedState = { holidayDrafts: state.holidayDrafts };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota / serialization errors — this is best-effort cache */
  }
};

const listeners = new Set<() => void>();
const emit = () => listeners.forEach((fn) => fn());
const subscribe = (fn: () => void) => {
  // The first screen to subscribe pulls the server data — nothing else in the
  // app imports this store, so this is the store's real point of first use.
  ensureHydrated();
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};
const getSnapshot = () => state;

// Mutate locally, write through to localStorage, then notify this tab.
const commit = (next: PayflowState) => {
  state = next;
  persist();
  emit();
};

const setSyncError = (message: string) => commit({ ...state, syncError: message });

const messageFor = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

// Cross-tab sync for the holiday drafts, which are all that is still kept locally.
// Decisions no longer need it: they are server state, so another tab's change is
// picked up by re-reading the payroll rather than by watching localStorage — and that
// now works across machines, which this never did.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key !== STORAGE_KEY || !e.newValue) return;
    try {
      const parsed: Partial<PersistedState> = JSON.parse(e.newValue);
      state = { ...state, holidayDrafts: parsed?.holidayDrafts || {} };
      emit();
    } catch {
      /* ignore malformed payloads */
    }
  });
}

/* ── Decisions: server state ⇄ the screens' global maps ───────────────── */

// A payroll id is the period's end date, so it is always exactly 10 characters. That
// makes splitting a global decision key back into its payroll and its server-side key a
// fixed offset rather than a parse — which matters, because employee ids contain
// dashes and roles contain spaces and colons, so nothing here could be split on a
// separator safely.
const PERIOD_ID_LENGTH = 10;

const splitKey = (key: string): { periodId: string; scoped: string } => ({
  periodId: key.slice(0, PERIOD_ID_LENGTH),
  scoped: key.slice(PERIOD_ID_LENGTH + 1),
});

// Drop every entry belonging to one payroll from a globally-keyed map.
const withoutPeriod = <T,>(map: Record<string, T>, prefix: string): Record<string, T> =>
  Object.fromEntries(Object.entries(map).filter(([k]) => !k.startsWith(prefix)));

const withoutKey = <T,>(map: Record<string, T>, key: string): Record<string, T> => {
  const next = { ...map };
  delete next[key];
  return next;
};

/**
 * Fold one payroll's server-held decisions into the store's global maps.
 *
 * Every map is first cleared of that payroll's entries and then refilled, so the
 * result is exactly what the server holds — a decision another admin removed
 * disappears here too, rather than lingering because nothing told us to delete it.
 */
const mergePeriodState = (
  periodId: string,
  server: PayrollDecisionState,
  version: string | null,
  // The period's frozen stage outputs, from the state fetch. Omitted by the action path
  // (an action response does not re-read them), which then leaves frozenStages untouched —
  // so an optimistic freeze set when verifying survives until the next authoritative load.
  frozen?: FrozenStagesResponse,
): Partial<PayflowState> => {
  const rowPrefix = `${periodId}-`;
  const scopePrefix = `${periodId}:`;

  const adjustments = withoutPeriod(state.adjustments, rowPrefix);
  for (const [key, value] of Object.entries(server.adjustments)) {
    adjustments[`${periodId}-${key}`] = { hours: value.hours, reason: value.reason };
  }

  const reviews = withoutPeriod(state.reviews, rowPrefix);
  for (const [key, value] of Object.entries(server.reviews)) {
    reviews[`${periodId}-${key}`] = {
      confirmedIn: value.confirmedIn,
      confirmedOut: value.confirmedOut,
      reason: value.reason,
      confirmedBy: value.confirmedBy,
      confirmedAt: value.confirmedAt,
    };
  }

  const poolSolo = withoutPeriod(state.poolSolo, scopePrefix);
  for (const [dayIndex, empIds] of Object.entries(server.poolSolo)) {
    poolSolo[`${periodId}:${dayIndex}`] = empIds;
  }

  const summaryOverrides = withoutPeriod(state.summaryOverrides, scopePrefix);
  for (const [key, value] of Object.entries(server.summaryOverrides)) {
    summaryOverrides[`${periodId}:${key}`] = value;
  }

  const lockOrNothing = (
    map: Record<string, HoursApproval>,
    lock: { by: string; at: string } | null,
  ): Record<string, HoursApproval> =>
    lock ? { ...map, [periodId]: { by: lock.by, at: lock.at } } : withoutKey(map, periodId);

  // Older docs stored one period-level pool ({resolved, groups}); the model is now one
  // pool per party ({parties}). Coerce anything without a parties map to empty so the
  // screens read it consistently — a pre-model doc simply has no party allocations yet.
  const normalizeManualPool = (mp: unknown): ManualPoolState =>
    mp && typeof mp === "object" && typeof (mp as any).parties === "object" && (mp as any).parties
      ? { parties: (mp as any).parties }
      : { parties: {} };

  return {
    adjustments,
    reviews,
    poolSolo,
    summaryOverrides,
    hoursApproved: lockOrNothing(state.hoursApproved, server.lifecycle.hours),
    tipsApproved: lockOrNothing(state.tipsApproved, server.lifecycle.tips),
    processedPeriods: lockOrNothing(state.processedPeriods, server.lifecycle.processed),
    manualPools: server.manualPool
      ? { ...state.manualPools, [periodId]: normalizeManualPool(server.manualPool) }
      : withoutKey(state.manualPools, periodId),
    barAllocations: { ...state.barAllocations, [periodId]: server.barAllocations ?? {} },
    staffAccounts: server.staffAccounts
      ? { ...state.staffAccounts, [periodId]: server.staffAccounts }
      : withoutKey(state.staffAccounts, periodId),
    // Only the state fetch carries `frozen`; the action path omits it and leaves
    // frozenStages as it was (preserving an optimistic freeze from a just-sent verify).
    ...(frozen !== undefined
      ? {
          frozenStages: {
            ...state.frozenStages,
            [periodId]: {
              hours: frozen.hours as FrozenStages["hours"],
              tips: frozen.tips,
              overview: frozen.overview,
            },
          },
        }
      : {}),
    periodVersion: { ...state.periodVersion, [periodId]: version },
    // Marked on the period itself, beside `daysLoaded`, because the screens need to
    // withhold the tables until BOTH have landed. Rows drawn before the decisions
    // arrive show unadjusted hours and unset locks, then jump when the fetch returns —
    // an accountant watching net hours change under them has no way to know which
    // number was the real one.
    payPeriods: state.payPeriods.map((p) =>
      p.id === periodId ? { ...p, decisionsLoaded: true } : p,
    ),
  };
};

// Forget everything we hold about one payroll's decisions — used when it is rebuilt or
// deleted, both of which discard them server-side.
const forgetPeriodState = (periodId: string): Partial<PayflowState> => ({
  adjustments: withoutPeriod(state.adjustments, `${periodId}-`),
  reviews: withoutPeriod(state.reviews, `${periodId}-`),
  poolSolo: withoutPeriod(state.poolSolo, `${periodId}:`),
  summaryOverrides: withoutPeriod(state.summaryOverrides, `${periodId}:`),
  hoursApproved: withoutKey(state.hoursApproved, periodId),
  tipsApproved: withoutKey(state.tipsApproved, periodId),
  processedPeriods: withoutKey(state.processedPeriods, periodId),
  manualPools: withoutKey(state.manualPools, periodId),
  barAllocations: withoutKey(state.barAllocations, periodId),
  staffAccounts: withoutKey(state.staffAccounts, periodId),
  registers: withoutKey(state.registers, periodId),
  frozenStages: withoutKey(state.frozenStages, periodId),
  periodVersion: withoutKey(state.periodVersion, periodId),
  payPeriods: state.payPeriods.map((p) =>
    p.id === periodId ? { ...p, decisionsLoaded: false } : p,
  ),
});

// In-flight state fetches, so two screens asking for the same payroll only hit the
// server once.
const stateRequests = new Map<string, Promise<void>>();

// `force` re-reads decisions this tab already holds. Needed when the local copy is
// suspect rather than missing — see the failure path in the action recorder below.
const loadPeriodState = (periodId: string, force = false): Promise<void> => {
  // Already held, and nothing has invalidated it.
  //
  // Without this the fetch repeats forever. `stateRequests` collapses only CONCURRENT
  // callers and is cleared the moment a request settles, so a caller driven by a React
  // effect re-fetches on every pass: the commit below rebuilds `payPeriods`, that new
  // array identity invalidates the memo the effect depends on, and the effect calls
  // here again. loadPeriodDetail has always had the equivalent `daysLoaded` guard —
  // the two are dispatched together, which is how only one of them spinning went
  // unnoticed.
  const period = state.payPeriods.find((p) => p.id === periodId);
  if (!force && period?.decisionsLoaded) return Promise.resolve();
  const inFlight = stateRequests.get(periodId);
  if (inFlight) return inFlight;
  const request = (async () => {
    try {
      const { state: server, version, frozen } = await fetchPayrollState(periodId);
      commit({ ...state, ...mergePeriodState(periodId, server, version, frozen ?? {}) });
    } catch (err) {
      setSyncError(
        messageFor(err, `Could not load the decisions for ${fmtISODate(periodId)}.`),
      );
    } finally {
      stateRequests.delete(periodId);
    }
  })();
  stateRequests.set(periodId, request);
  return request;
};

/* ── Recording a decision ─────────────────────────────────────────────── */

// A stable, unique id per gesture. The server writes the audit event under an id
// containing it, so a retry — a double-click, a dropped response — cannot apply the
// same change twice.
let actionCounter = 0;
const newActionId = (): string => {
  actionCounter += 1;
  const random = Math.random().toString(36).slice(2, 10);
  return `${Date.now().toString(36)}-${random}-${actionCounter}`;
};

/**
 * Apply a decision locally, then record it on the server.
 *
 * The optimistic write keeps the screens instant — an accountant ticking through
 * exception rows should never wait on a round trip. What comes back replaces it, so
 * the server's version of events always wins in the end, including the parts this tab
 * could not have known (another admin's edit, a lock someone else set).
 *
 * On failure the whole payroll's decisions are re-read rather than rolled back to a
 * remembered value: the request may have succeeded and lost its response, and asking is
 * the only way to find out which.
 */
const runAction = async (
  periodId: string,
  optimistic: Partial<PayflowState>,
  // `Pick` is not redundant. `PayrollActionRequest` carries an index signature, so its
  // `keyof` is `string`, so `Omit` cannot subtract a single literal key from it and
  // returns the index signature ALONE — `type` and `by` stop being known properties and
  // the spread below no longer satisfies the request type. Restoring them is what keeps
  // that spread checked rather than silently accepted.
  action: Omit<PayrollActionRequest, "clientActionId"> &
    Pick<PayrollActionRequest, "type" | "by">,
): Promise<void> => {
  const previous = state;
  commit({ ...state, ...optimistic, syncError: null });
  try {
    const { state: server, version } = await postPayrollAction(periodId, {
      ...action,
      clientActionId: newActionId(),
      ...(action.expectedVersion === undefined
        ? { expectedVersion: state.periodVersion[periodId] ?? undefined }
        : {}),
    });
    commit({ ...state, ...mergePeriodState(periodId, server, version) });
  } catch (err) {
    state = previous;
    emit();
    setSyncError(messageFor(err, "Could not save that change."));
    // Whatever the server actually holds is the truth; the local revert above is only
    // to stop the screen showing something that was refused. Forced, because the
    // decisions ARE loaded — they are just no longer trustworthy, which is exactly the
    // case the freshness guard cannot recognise on its own.
    await loadPeriodState(periodId, true);
  }
};

/* ── Server sync ──────────────────────────────────────────────────────── */

const mapDay = (d: PayrollDay): Day => ({
  index: d.index,
  weekday: weekdayOfISO(d.dateISO),
  dateLabel: fmtISODate(d.dateISO),
  dateISO: d.dateISO,
  rows: d.rows,
});

// What has been fetched for a period beyond its summary. Omitted entirely for a
// payroll nothing has been loaded for yet.
interface PeriodDetail {
  days: Day[];
  warnings: string[];
  decisionsLoaded: boolean;
}

const mapPayroll = (p: PayrollSummary, detail?: PeriodDetail): PayPeriod => ({
  id: p.id,
  name: `Pay period ending ${fmtISODate(p.periodEnd)}`,
  start: ymdFromISO(p.periodStart),
  endISO: p.periodEnd,
  stage: "ready",
  statusDate: fmtTimestampDate(p.createdAt),
  daysLoaded: detail !== undefined,
  decisionsLoaded: detail?.decisionsLoaded ?? false,
  days: detail?.days ?? [],
  warnings: detail?.warnings ?? [],
  ...(p.rulesSnapshot ? { rules: p.rulesSnapshot } : {}),
});

// `decisionsLoaded` is the caller's to supply: the days and the decisions are separate
// requests, so landing one says nothing about the other.
const mapPayrollDoc = (doc: PayrollDoc, decisionsLoaded: boolean): PayPeriod =>
  mapPayroll(doc, { days: doc.days.map(mapDay), warnings: doc.warnings ?? [], decisionsLoaded });

const byPeriodEndDesc = (a: PayPeriod, b: PayPeriod): number =>
  b.endISO.localeCompare(a.endISO);

const applyRules = (rules: PayflowRules): Partial<PayflowState> => ({
  tipRates: rules.tipRates,
  tipRatesLock: rules.tipRatesLock,
  breakRules: rules.breakRules,
  breakRulesLock: rules.breakRulesLock,
  fohRoles: rules.fohRoles,
  fohRolesLock: rules.fohRolesLock,
  salariedEmployeeIds: rules.salariedEmployeeIds ?? [],
  salariedLock: rules.salariedLock ?? null,
  committedHolidays: rules.committedHolidays,
  payConstants: rules.payConstants,
  payPeriod: rules.payPeriod,
});

const rulesFromState = (s: PayflowState): PayflowRules => ({
  tipRates: s.tipRates,
  tipRatesLock: s.tipRatesLock,
  breakRules: s.breakRules,
  breakRulesLock: s.breakRulesLock,
  fohRoles: s.fohRoles,
  fohRolesLock: s.fohRolesLock,
  salariedEmployeeIds: s.salariedEmployeeIds,
  salariedLock: s.salariedLock,
  committedHolidays: s.committedHolidays,
  payConstants: s.payConstants,
  payPeriod: s.payPeriod,
});

// The etag from the last rules read, sent back as If-Match so a concurrent
// admin edit is rejected instead of silently overwritten.
let rulesEtag: string | null = null;

// Pull the server's rules back over the local copy — used after a 409, where
// the server's version is authoritative and the local edit was not saved.
const reloadRules = async (conflictMessage: string) => {
  try {
    const res = await fetchRules();
    rulesEtag = res.etag;
    commit({ ...state, ...applyRules(res.rules), syncError: conflictMessage });
  } catch (err) {
    setSyncError(messageFor(err, "Could not reload the payroll rules."));
  }
};

// Write the current rules to the server. Called by every commit/unlock action;
// the draft-edit actions stay local until one of those fires.
const persistRules = async (note: string) => {
  try {
    const saved = await saveRules(rulesFromState(state), rulesEtag, note);
    rulesEtag = saved.etag;
    // The server writes the audit entry for the change, so re-read rather than
    // inventing a local one that could disagree with it.
    void payflowActions.loadAuditLog();
  } catch (err) {
    if (err instanceof PayflowApiError && err.status === 409) {
      await reloadRules(
        "Payroll rules were changed by someone else, so your change was not saved. " +
          "The current rules have been reloaded — reapply your change.",
      );
      return;
    }
    setSyncError(messageFor(err, "Could not save the payroll rules."));
  }
};

const pullFromServer = async (): Promise<void> => {
  try {
    const [payrolls, rules] = await Promise.all([listPayrolls(), fetchRules()]);
    rulesEtag = rules.etag;
    const loaded = new Map<string, PeriodDetail>(
      state.payPeriods
        .filter((p) => p.daysLoaded)
        .map((p) => [
          p.id,
          { days: p.days, warnings: p.warnings, decisionsLoaded: p.decisionsLoaded },
        ]),
    );
    // Everything the server lists is a finished payroll — it excludes anything the old
    // asynchronous builder left mid-flight — so there is nothing to filter out here.
    const payPeriods = payrolls
      .map((p) => mapPayroll(p, loaded.get(p.id)))
      .sort(byPeriodEndDesc);
    commit({
      ...state,
      ...applyRules(rules.rules),
      payPeriods,
      hydrated: true,
      syncError: null,
    });
    // The audit log is org-wide and server-held, so it is loaded alongside the
    // payrolls rather than only when the Audit Logs tab is opened — the tab shows it
    // immediately, and an entry another admin wrote is visible here without a reload.
    void payflowActions.loadAuditLog();
  } catch (err) {
    commit({
      ...state,
      hydrated: true,
      syncError: messageFor(err, "Could not load PayFlow data from the server."),
    });
  }
};

// Concurrent callers (both screens mounting, a screen re-opening) share one
// round trip rather than racing each other's writes.
let refreshInFlight: Promise<void> | null = null;

// Pull the payroll list + rules. Days are fetched per payroll, so any detail
// already loaded in this tab is carried over.
export function refreshFromServer(): Promise<void> {
  if (!refreshInFlight) {
    refreshInFlight = pullFromServer().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

let firstLoadStarted = false;
const ensureHydrated = () => {
  if (firstLoadStarted) return;
  firstLoadStarted = true;
  void refreshFromServer();
};

// In-flight detail fetches, so two screens asking for the same payroll only
// hit the server once.
const detailRequests = new Map<string, Promise<void>>();

const loadPeriodDetail = (id: string): Promise<void> => {
  const period = state.payPeriods.find((p) => p.id === id);
  if (!period || period.daysLoaded) return Promise.resolve();
  const inFlight = detailRequests.get(id);
  if (inFlight) return inFlight;
  const request = (async () => {
    try {
      const doc = await fetchPayroll(id);
      commit({
        ...state,
        // The decisions are a separate request and may already have landed, so carry
        // that flag across rather than resetting it — otherwise the two fetches would
        // each undo the other's progress and the spinner would never clear.
        payPeriods: state.payPeriods.map((p) =>
          p.id === id ? mapPayrollDoc(doc, p.decisionsLoaded) : p,
        ),
      });
    } catch (err) {
      setSyncError(
        messageFor(err, `Could not load the payroll for ${fmtISODate(id)}.`),
      );
    } finally {
      detailRequests.delete(id);
    }
  })();
  detailRequests.set(id, request);
  return request;
};

/* ── Payroll build ────────────────────────────────────────────────────── */
// Creating a payroll is one request. The server assembles it from data already stored
// and answers with the finished document, so there is nothing to poll, no deadline to
// outlast, and no build to resume on load — a build either completed inside its request
// or it never happened.

// Shared with the data-pull watch below, which is the one loop here that really is
// waiting on something long-running. Declared at this level so removing anything from
// the build path cannot quietly take it away.
const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// A finished build: replace the period in the list and clear the progress.
//
// A rebuild discards the payroll's decisions — the server does that, and records that
// it did — so the local copy of them is dropped to match. They cannot be carried
// across: shift ordinals are assigned by sort order, so a shift that arrives late and
// starts earlier renumbers the ones after it, and a decision keyed by row would end up
// pointing at a different shift.
const landPayroll = (doc: PayrollDoc) => {
  // A rebuild discards the decisions server-side, so this payroll genuinely has none
  // and the fetch below only confirms it — but the flag starts false so the screen
  // waits for that confirmation rather than showing an empty decision set as fact.
  const period = mapPayrollDoc(doc, false);
  const others = state.payPeriods.filter((p) => p.id !== period.id);
  commit({
    ...state,
    ...forgetPeriodState(period.id),
    payPeriods: [...others, period].sort(byPeriodEndDesc),
    // The rebuild just took a fresh fingerprint, so any drift we had recorded
    // describes a payroll that no longer exists. Dropped rather than kept, so the
    // screen asks again instead of showing a warning about the previous build.
    drift: Object.fromEntries(Object.entries(state.drift).filter(([id]) => id !== period.id)),
    building: null,
    syncError: null,
    // A build that lands clears any earlier "no data for that period" wall.
    coverageGap: null,
  });
  // The audit entry for the rebuild is written server-side, so re-read the log rather
  // than inventing a local one that would disagree with it.
  void payflowActions.loadAuditLog();
  // And confirm the (now empty) decision set, which clears `decisionsLoaded`.
  void loadPeriodState(period.id);
};

/* ── Data pull (start + poll) ─────────────────────────────────────────── */
// A pull is org-wide: another admin or the nightly schedule can start one, and this
// tab may be watching a pull it did not begin. So whether a pull is RUNNING is the
// server's answer and never a local flag. The one exception is `dataPullWatch`, which
// covers the gap before the server has been asked at all — see below.
//
// Every loop here is bounded, and by three separate limits rather than one. They stop
// different things: a run that takes too long, a run we can no longer see, and the
// moment right after starting when it is normal not to see it yet. A single timeout
// would have to be the longest of the three, which would leave the other two polling a
// dead endpoint for the better part of an hour.

// A pull takes minutes and its phases last minutes, so polling faster than this buys
// nothing but requests: at the original 5s a 35-minute run cost 420 round trips per
// watching tab, each one re-reading Cosmos. At 20s it is 105 — three requests a minute
// rather than twelve.
//
// The cost of the slower cadence is bounded and small: a finished run is noticed up to
// one interval later, because asking is the only way to learn it finished. Twenty
// seconds of spinner on a job that takes minutes is not worth four times the traffic.
//
// What this does NOT affect is whether polling stops. That is decided by the run doc
// reaching a terminal status, and the loop exits on the first read that sees one.
const PULL_POLL_MS = 20_000;

// Stop watching a run that is still going after this long. The pipeline's own run lease
// defaults to 35 minutes (RECON_RUN_LEASE_SECONDS in variance-etl-pipeline/app/config.py)
// and a run cannot outlive its lease, so past that there is nothing left to watch: the
// run either finished or was superseded. The extra five minutes is slack for a lease
// renewed just before it lapsed.
//
// This gives up on WATCHING, never on the run. The pull carries on server-side and the
// next status read picks it back up.
const PULL_WATCH_TIMEOUT_MS = 40 * 60 * 1000;

// How long the server may be unable to read ReconState before we stop watching.
//
// Short, because this is not a slow run — it is a broken lookup, and every extra poll
// asks a question that has already failed. Two minutes rides out a restart or a
// transient Cosmos fault; a misconfigured database never recovers, and polling it for
// forty minutes only delays telling someone.
const RUN_BLIND_LIMIT_MS = 2 * 60_000;

// Bumped whenever a watch starts, so a superseded poll loop exits rather than
// writing stale progress over a newer pull's.
let pullWatchToken = 0;

const isPullRunning = (status: DataPullStatus | null): boolean => status?.state === "running";

// What survives a successful read. A transient problem was about the reading itself,
// so a read that worked has just disproved it; a start failure is about an action and
// is not ours to retract.
const errorAfterRead = (problem: DataPullProblem | null): DataPullProblem | null =>
  problem?.transient ? null : problem;

// How long a started run may stay invisible before that is treated as a fault rather
// than as replication lag.
//
// The pipeline writes the run doc BEFORE it answers 202, so in a correctly configured
// deployment the very first poll should already see it. The allowance exists because
// the writer and the reader are different Cosmos clients holding different session
// tokens, and a read taken moments after a write on another session can legitimately
// miss it. Thirty seconds is well past that and well short of anything a person would
// sit and watch.
const RUN_CONFIRM_GRACE_MS = 30_000;

// Whether this tab still has something to poll for: a run of its own, or one somebody
// else started that the server says is live.
const stillWatching = (s: PayflowState): boolean =>
  s.dataPullWatch !== null || isPullRunning(s.dataPull);

// The run this tab started cannot be found and the grace period is over. This message
// is what used to be a silent revert to the calendar.
//
// It names the two things that actually cause it, because both are configuration and
// neither is visible from this screen: the run doc is keyed by orgID and lives in
// whichever database the PIPELINE writes to, and PayFlow reads it from whichever
// database IT is pointed at, under the org on the caller's token. If those disagree,
// every run is real, succeeds, and is completely unobservable.
const lostRunProblem = (runId: string, detail?: string): DataPullProblem => ({
  message:
    `The pipeline accepted this pull (run ${runId}) but PayFlow cannot find it. ` +
    `The pull is most likely still running, so this is a reporting fault rather than a ` +
    `failed pull — starting another one will not help. This normally means the ` +
    `pipeline and PayFlow are reading different databases, or the run was recorded ` +
    `under a different organisation.` +
    (detail ? ` Server: ${detail}` : ""),
  transient: false,
});

// A different failure from the one above, and it needs different advice. There the
// progress record was readable and did not hold our run; here it could not be read at
// all, so nothing is known about the run either way and the honest instruction is to
// look again later rather than to go checking configuration.
const blindProblem = (runId: string, detail?: string): DataPullProblem => ({
  message:
    `PayFlow has stopped following this pull (run ${runId}) — it could not read the ` +
    `pipeline's progress record for ${Math.round(RUN_BLIND_LIMIT_MS / 60_000)} minutes. ` +
    `The pull itself is unaffected and is probably still running; reload in a few ` +
    `minutes to pick it back up.` +
    (detail ? ` Server: ${detail}` : ""),
  transient: false,
});

/**
 * Reconciles what the server just said with the run this tab is waiting on.
 *
 * Split out because it is the entire correctness argument for the pull modal, and
 * because it is pure: given a status and a watch, what should this tab now believe.
 * The old code had no equivalent. It asked "is the server running something", and threw
 * away both the run id and the difference between "no" and "I cannot tell" — so a pull
 * whose run doc it could not see was indistinguishable from one that never started.
 */
const reconcileWatch = (
  watch: PullWatch | null,
  status: DataPullStatus,
  now: number,
): { watch: PullWatch | null; problem?: DataPullProblem } => {
  // Nothing of ours in flight. Whatever the server reports is simply adopted, which is
  // how this tab picks up the nightly run or another admin's.
  if (!watch) return { watch: null };
  // The POST has not answered, so there is no run id to match on and nothing the server
  // could say that would be about our run.
  if (watch.phase === "starting") return { watch };

  // The server could not read ReconState. Not evidence either way, so the watch stands
  // — this is precisely the state that must never be read as "no run". But it is
  // bounded: a lookup that keeps failing is a fault, not a slow run, and polling it
  // until the 40-minute run deadline would ask a broken question 240 times and tell
  // nobody until the end.
  if (status.state === "unknown") {
    const blindSince = watch.blindSince ?? now;
    if (now - blindSince >= RUN_BLIND_LIMIT_MS) {
      return { watch: null, problem: blindProblem(watch.runId, status.detail) };
    }
    return { watch: { ...watch, blindSince } };
  }

  // A conclusive read. Whatever was wrong has cleared, so the blindness clock resets
  // rather than accumulating across unrelated blips.
  if (status.runId === watch.runId) {
    if (status.state === "running") {
      return { watch: { phase: "watching", runId: watch.runId, blindSince: null } };
    }
    // Terminal. Our run is done and there is nothing left to watch; the modal reports
    // the outcome from `dataPull` like any other finished run.
    return { watch: null };
  }

  // A different run holds the doc — ours lost the lease, or never took it. Either way
  // this tab is no longer watching what it believes it is.
  if (status.runId) {
    return {
      watch: null,
      problem: {
        message: "Another data update replaced this one. Check the pull running now.",
        transient: false,
      },
    };
  }

  // ReconState is readable and holds no run, while we hold a runId the pipeline handed
  // us. Allow for cross-client replication lag, then call it.
  if (watch.phase === "confirming" && now - watch.since < RUN_CONFIRM_GRACE_MS) {
    return { watch: { ...watch, blindSince: null } };
  }
  return { watch: null, problem: lostRunProblem(watch.runId, status.detail) };
};

// Read the status once and reconcile it against the run this tab is waiting on. Never
// throws — a failed read is reported on the state.
//
// Note what this does NOT do: clear the error unconditionally, or conclude anything
// from a response it could not read. It used to do both, which meant startDataPull
// could set "someone else is already pulling", call this one line later, and have the
// message erased before anyone read it — and then the watch loop erased it again every
// five seconds for good measure.
const readPullStatus = async (): Promise<void> => {
  try {
    const status = await fetchDataPullStatus();
    const { watch, problem } = reconcileWatch(state.dataPullWatch, status, Date.now());
    commit({
      ...state,
      dataPull: status,
      dataPullWatch: watch,
      dataPullError: problem ?? errorAfterRead(state.dataPullError),
    });
  } catch (err) {
    // The read itself failed. It says nothing about the run, so the watch is left
    // exactly as it was and the next poll tries again.
    commit({
      ...state,
      dataPullError: {
        message: messageFor(err, "Could not read the data pull status."),
        transient: true,
      },
    });
  }
};

const watchPull = async (token: number): Promise<void> => {
  const deadline = Date.now() + PULL_WATCH_TIMEOUT_MS;
  for (;;) {
    await delay(PULL_POLL_MS);
    if (pullWatchToken !== token) return;
    await readPullStatus();
    if (pullWatchToken !== token) return;
    // Keyed off the reconciled state rather than off "did the server say running".
    // A status this tab could not read, or one that has not caught up with the run we
    // started, leaves the watch in place and the loop keeps going — which is the whole
    // difference between waiting for a slow run and abandoning a live one.
    if (!stillWatching(state)) {
      // Nothing left to follow. Every cached badge is now a claim about data as it was
      // BEFORE the pull, so the cache is dropped rather than refreshed: the calendar
      // re-asks for whichever month is on screen, and months nobody looks at cost
      // nothing.
      commit({ ...state, dateCoverage: {}, dateCoverageLoaded: [] });
      return;
    }
    if (Date.now() > deadline) {
      commit({
        ...state,
        // Not transient: this is the end of the watch, so no later read will come
        // along to clear it, and the advice it carries stays true until acted on.
        dataPullError: {
          message:
            "The data pull is still going after 40 minutes. It may still finish — reload the page to check.",
          transient: false,
        },
      });
      return;
    }
  }
};

export const payflowActions = {
  /* ── Data pull ── */
  // Read the current pull status and, if one is running, keep watching it. Safe to
  // call on mount and on focus; concurrent watches supersede rather than stack.
  async loadDataPull(): Promise<void> {
    await readPullStatus();
    if (stillWatching(state)) {
      const token = ++pullWatchToken;
      void watchPull(token);
    }
  },
  // Ask for a fresh pull and watch it by run id. A 409 (someone else is already
  // pulling) is reported like any other failure and then reconciled by reading the real
  // status, so the screen ends up showing the pull that IS running rather than an error.
  //
  // `window` is passed through untouched. Which dates are offerable is a screen
  // decision (it depends on the pay-period anchor); the server validates them again
  // regardless, so nothing here re-checks them.
  async startDataPull(window?: DataPullWindow): Promise<void> {
    // Both guards, and they cover different windows. `isPullRunning` catches a pull the
    // server has already told us about — including one another admin started. The watch
    // catches the one it has not: between pressing the button and the 202 coming back
    // there are two round trips during which the server's answer is still "nothing is
    // running", and without this a second press would start a second run.
    if (isPullRunning(state.dataPull) || state.dataPullWatch) return;

    // Committed before the first await, so the button is disabled in the same tick as
    // the click rather than a network round trip later.
    commit({ ...state, dataPullError: null, dataPullWatch: { phase: "starting" } });

    let runId: string;
    try {
      ({ runId } = await requestDataPull(window));
    } catch (err) {
      // Not transient: this is the outcome of something the admin did, and it stays
      // until they dismiss it or try again. The read that follows reconciles the screen
      // with what is really running — for a 409 that means the modal switches to
      // watching someone else's pull — and the message survives it.
      commit({
        ...state,
        dataPullError: {
          message: messageFor(err, "Could not start the data pull."),
          transient: false,
        },
        dataPullWatch: null,
      });
      await readPullStatus();
      return;
    }

    // The run id is the point of all this. The pipeline guarantees the run doc exists
    // before it answers, so from here on "the server reports no run" is a statement
    // about our ability to see it, not about whether it is happening — and only the id
    // lets those be told apart. A 202 without one is a contract violation the transport
    // already rejects, so an empty string here means something upstream lied; watch it
    // anyway rather than dropping the run, and let the grace window report it.
    commit({
      ...state,
      dataPullWatch: { phase: "confirming", runId, since: Date.now(), blindSince: null },
    });

    await readPullStatus();
    const token = ++pullWatchToken;
    void watchPull(token);
  },
  dismissDataPullError(): void {
    commit({ ...state, dataPullError: null });
  },
  /**
   * Load which dates have data for one month, once.
   *
   * Per month rather than for the whole selectable range: the calendar shows one month
   * at a time and an admin usually looks at one or two, so fetching a year up front
   * would be an aggregate over ~365 dates to render 30 badges.
   *
   * A month already asked for is never asked for again, including one whose request
   * failed. Retrying on every render would turn a broken endpoint into a request loop,
   * and the badges are an aid — a month without them still selects normally.
   */
  async loadDateCoverage(monthKey: string, startISO: string, endISO: string): Promise<void> {
    if (state.dateCoverageLoaded.includes(monthKey)) return;
    // Marked before the await, or a calendar that renders twice before the response
    // lands fires the request twice.
    commit({ ...state, dateCoverageLoaded: [...state.dateCoverageLoaded, monthKey] });
    try {
      const dates = await fetchDateCoverage(startISO, endISO);
      const dateCoverage = { ...state.dateCoverage };
      for (const entry of dates) dateCoverage[entry.date] = entry;
      commit({ ...state, dateCoverage });
    } catch (err) {
      // Deliberately not surfaced on `dataPullError`: that banner is about pulling
      // data, and a missing badge is not a reason to tell someone their pull failed.
      // The month simply stays unmarked.
      console.warn("[payflow] could not load date coverage:", err);
    }
  },

  /* ── Payrolls ── */
  // Whether a payroll already exists for a period-ending date, asked of the server
  // rather than answered from the loaded list. The list is a snapshot and does not see
  // another admin's work, and the answer decides whether creating one silently
  // replaces a finished payroll. Throws if the check itself fails — the caller decides
  // what to do without an answer.
  async payrollExists(endISO: string): Promise<boolean> {
    try {
      await fetchPayroll(endISO);
      return true;
    } catch (err) {
      if (err instanceof PayflowApiError && err.status === 404) return false;
      throw err;
    }
  },
  // Fetch a payroll's days and its decisions on first use (the list endpoint omits
  // both). They are fetched together because nothing on a payroll screen is correct
  // with one and not the other: rows without decisions show unadjusted hours, and an
  // unlocked-looking period that is in fact verified invites an edit that will be
  // refused.
  loadPeriodDetail(id: string): Promise<void> {
    return Promise.all([loadPeriodDetail(id), loadPeriodState(id)]).then(() => undefined);
  },
  // Fetch every payroll's days and decisions — the manager screen needs all of them to
  // find its outstanding tasks, and stat pay reaches back four weeks into the payroll
  // before the open one. A prior period loaded WITHOUT its decisions would compute
  // smaller net hours and quietly change the stat-pay figure, with nothing on screen
  // to say so.
  loadAllPeriodDetails(): Promise<void> {
    // Both halves decline the round trip when they already hold what they would fetch,
    // so this asks for all of them and lets each one decide.
    return Promise.all(
      state.payPeriods.flatMap((p) => [loadPeriodDetail(p.id), loadPeriodState(p.id)]),
    ).then(() => undefined);
  },
  // The audit log, newest first, straight from the server. Org-wide: entries outlive
  // the payroll they describe, including decisions a rebuild discarded.
  async loadAuditLog(): Promise<void> {
    try {
      const events = await fetchPayflowEvents();
      commit({
        ...state,
        auditLog: events.map((event) => ({
          at: event.at,
          // The name the actor had on screen. The identity of record is the user id
          // the server took from the access token; this is only what to show.
          by: event.byDisplay || event.byUserID,
          action: event.action,
          reason: event.reason,
          ip: event.ip,
          userAgent: event.userAgent,
        })),
      });
    } catch (err) {
      // A missing audit log is not a reason to make the payroll screens look broken.
      console.warn("[payflow] could not load the audit log", err);
    }
  },
  // Ask whether a payroll's source records have changed since it was built.
  //
  // On demand, not on load: the answer usually costs two point reads, but on a payroll
  // whose data HAS been pulled since, it reads the whole period's source records — not
  // something to do for every payroll every time the list renders.
  //
  // A failure is swallowed to a console warning rather than raised into the sync
  // banner. Not knowing whether the source moved is a missing reassurance, not a
  // broken payroll, and it must not make the screen look like something is wrong.
  async loadDrift(id: string): Promise<void> {
    try {
      const report = await fetchPayrollDrift(id);
      commit({ ...state, drift: { ...state.drift, [id]: report } });
    } catch (err) {
      console.warn(`[payflow] could not check ${id} for source data changes`, err);
    }
  },

  // The newest stored register for a processed period — what was actually paid. Loaded
  // on demand when a processed period is viewed, so the Summary tab can render the frozen
  // figures rather than recomputing them. Held as `null` when the period has no register
  // (processed before registers existed), so this never refetches a known-empty one. A
  // failure is swallowed to a warning: the live fallback still renders.
  async loadRegister(id: string): Promise<void> {
    if (state.registers[id] !== undefined) return;
    try {
      const registers = await fetchPayrollRegisters(id);
      commit({ ...state, registers: { ...state.registers, [id]: registers[0] ?? null } });
    } catch (err) {
      console.warn(`[payflow] could not load the register for ${id}`, err);
    }
  },

  // Build a payroll for a period-ending date. `replace` rebuilds an existing payroll
  // (its decisions are discarded server-side).
  //
  // One request, and its response IS the payroll — no poll, and no second fetch to get
  // the rows the old summary response could not carry. `state.building` is the whole of
  // the double-submit guard: it is committed before the await, so a second click in the
  // same tick finds it already set.
  async createPayroll(endISO: string, replace: boolean): Promise<void> {
    if (state.building) return;
    commit({ ...state, building: endISO, syncError: null, coverageGap: null });
    try {
      landPayroll(await requestPayrollBuild(endISO, replace));
    } catch (err) {
      // Covers the refusal to build an uncovered period as well as a genuine failure.
      // The server's own wording is preferred (see ERROR_TEXT in payflowApi), because
      // for `not-covered` it names the action that fixes it rather than reporting a
      // fault: nothing went wrong and nothing was written.
      //
      // A `not-covered` refusal is recorded as a coverage gap as well as a
      // message, so the banner can offer to pull that exact fortnight and build
      // it — the one thing a build cannot do for itself.
      const message = messageFor(err, `Could not create the payroll for ${fmtISODate(endISO)}.`);
      const notCovered = err instanceof PayflowApiError && err.code === "not-covered";
      commit({
        ...state,
        building: null,
        syncError: message,
        coverageGap: notCovered ? { endISO } : null,
      });
    }
  },
  // Delete a payroll server-side, then drop it locally.
  async deletePeriod(id: string, _by: string, _subject: string): Promise<void> {
    try {
      await requestPayrollDelete(id);
    } catch (err) {
      setSyncError(messageFor(err, "Could not delete the payroll."));
      return;
    }
    commit({
      ...state,
      ...forgetPeriodState(id),
      payPeriods: state.payPeriods.filter((p) => p.id !== id),
    });
    // The server records the deletion, and keeps the events belonging to the payroll
    // that is now gone — which is the point of an audit log.
    void payflowActions.loadAuditLog();
  },
  clearSyncError() {
    if (state.syncError === null && state.coverageGap === null) return;
    commit({ ...state, syncError: null, coverageGap: null });
  },

  /* ── Decisions ──
     Every one of these applies its change locally first, so the screen responds at
     once, then records it against the payroll on the server. The signatures are
     unchanged — each caller in ManagePayFlow and PayFlow passes exactly what it always
     did — and none of this is visible to the payroll arithmetic. */

  setAdjustment(key: string, hours: number, reason: string, by: string, subject: string) {
    const { periodId, scoped } = splitKey(key);
    const signed = `${hours > 0 ? "+" : ""}${hours.toFixed(2)}h`;
    void runAction(
      periodId,
      { adjustments: { ...state.adjustments, [key]: { hours, reason } } },
      { type: "adjustment.set", by, subject: `${subject} (${signed})`, reason, rowKey: scoped, hours },
    );
  },
  clearAdjustment(key: string, by: string, subject: string) {
    const { periodId, scoped } = splitKey(key);
    void runAction(
      periodId,
      { adjustments: withoutKey(state.adjustments, key) },
      { type: "adjustment.clear", by, subject, rowKey: scoped },
    );
  },
  confirm(
    key: string,
    confirmedIn: number,
    confirmedOut: number,
    reason: string,
    confirmedBy: string,
    subject: string,
  ) {
    const { periodId, scoped } = splitKey(key);
    void runAction(
      periodId,
      {
        reviews: {
          ...state.reviews,
          [key]: {
            confirmedIn,
            confirmedOut,
            reason,
            confirmedBy,
            confirmedAt: new Date().toISOString(),
          },
        },
      },
      {
        type: "review.confirm",
        by: confirmedBy,
        subject,
        reason,
        rowKey: scoped,
        confirmedIn,
        confirmedOut,
      },
    );
  },
  reopen(key: string, by: string, subject: string) {
    const { periodId, scoped } = splitKey(key);
    void runAction(
      periodId,
      { reviews: withoutKey(state.reviews, key) },
      { type: "review.reopen", by, subject, rowKey: scoped },
    );
  },

  /* ── Hours verification (locks Net Hours for a period) ── */
  // `freeze` is the Hours tab's computed output (rowKey → row result) at the moment of
  // verification. Sent with the action and stored verbatim, so the verified Hours tab
  // renders what was signed off rather than a recomputation a later engine change could
  // move. Applied optimistically too, so the lock and its frozen figures land together.
  approveHours(
    periodId: string,
    by: string,
    subject: string,
    freeze?: Record<string, RowComputation>,
  ) {
    void runAction(
      periodId,
      {
        hoursApproved: { ...state.hoursApproved, [periodId]: { by, at: new Date().toISOString() } },
        ...(freeze
          ? {
              frozenStages: {
                ...state.frozenStages,
                [periodId]: { ...state.frozenStages[periodId], hours: freeze },
              },
            }
          : {}),
      },
      { type: "hours.approve", by, subject, ...(freeze ? { freeze } : {}) },
    );
  },
  unlockHours(periodId: string, reason: string, by: string, subject: string) {
    void runAction(
      periodId,
      { hoursApproved: withoutKey(state.hoursApproved, periodId) },
      { type: "hours.unlock", by, subject, reason },
    );
  },

  /* ── Tips verification (per period) ── */
  // `freeze` is the Tips tab's computed output (dayIndex → the day's tip-out entries) at
  // the moment of verification, stored verbatim so the verified Tips tab renders what was
  // signed off rather than a recomputation. Applied optimistically alongside the lock.
  approveTips(periodId: string, by: string, subject: string, freeze?: unknown) {
    void runAction(
      periodId,
      {
        tipsApproved: { ...state.tipsApproved, [periodId]: { by, at: new Date().toISOString() } },
        ...(freeze !== undefined
          ? {
              frozenStages: {
                ...state.frozenStages,
                [periodId]: { ...state.frozenStages[periodId], tips: freeze },
              },
            }
          : {}),
      },
      { type: "tips.approve", by, subject, ...(freeze !== undefined ? { freeze } : {}) },
    );
  },
  unlockTips(periodId: string, reason: string, by: string, subject: string) {
    void runAction(
      periodId,
      { tipsApproved: withoutKey(state.tipsApproved, periodId) },
      { type: "tips.unlock", by, subject, reason },
    );
  },

  /* ── Tip pools ── */
  // Remove/restore a member from their pool on a given day (untick in Edit). The whole
  // day's list is sent, not the toggle: the server stores what the pool IS, so two
  // admins toggling different people cannot compose into a list neither of them chose.
  togglePoolMember(periodId: string, dayIndex: number, empId: string, by: string = "") {
    const k = poolDayKey(periodId, dayIndex);
    const current = state.poolSolo[k] || [];
    const empIds = current.includes(empId)
      ? current.filter((id) => id !== empId)
      : [...current, empId];
    void runAction(
      periodId,
      { poolSolo: { ...state.poolSolo, [k]: empIds } },
      { type: "poolSolo.set", by, subject: `day ${dayIndex + 1}`, dayIndex, empIds },
    );
  },

  /* ── Manual (party) tip pools ──
     One pool per Event-account party (keyed dateISO|Event N). The whole parties map is
     sent on every change; the server stores it verbatim. */
  // Build/confirm a single party's pool — its role-band groups and their percentages.
  setPartyPool(
    periodId: string,
    key: string,
    groups: ManualPoolGroup[],
    by: string,
    subject: string,
  ) {
    const existing = state.manualPools[periodId];
    const parties = { ...(existing?.parties ?? {}) };
    parties[key] = { resolved: true, by, at: new Date().toISOString(), groups };
    const manualPool: ManualPoolState = { parties };
    void runAction(
      periodId,
      { manualPools: { ...state.manualPools, [periodId]: manualPool } },
      { type: "manualPool.set", by, subject: `${groups.length} group(s) — ${subject}`, manualPool },
    );
  },
  // Reopen one party's pool for editing (keeps its groups, back to pending).
  reopenPartyPool(periodId: string, key: string, by: string, subject: string) {
    const existing = state.manualPools[periodId];
    const current = existing?.parties?.[key];
    if (!current) return;
    const parties = { ...existing!.parties, [key]: { ...current, resolved: false } };
    const manualPool: ManualPoolState = { parties };
    void runAction(
      periodId,
      { manualPools: { ...state.manualPools, [periodId]: manualPool } },
      { type: "manualPool.set", by, subject: `reopened — ${subject}`, manualPool },
    );
  },

  /* ── Bar tip-out allocation (no bartender on Main Bar) ──
     Server-to-bar money charged in a window with no Main Bar bartender to receive it. The
     manager picks who gets it — a set of employee ids (empty = nobody, servers pay none) —
     which clears the task and unblocks Verify Tips. Keyed `${dateISO}|${window}`. */
  setBarAllocation(
    periodId: string,
    key: string,
    recipients: string[],
    by: string,
    subject: string,
  ) {
    const existing = state.barAllocations[periodId] ?? {};
    const next = { ...existing, [key]: { recipients, by, at: new Date().toISOString() } };
    void runAction(
      periodId,
      { barAllocations: { ...state.barAllocations, [periodId]: next } },
      { type: "barAllocation.set", by, subject, key, recipients },
    );
  },

  /* ── Staff Accounts report (manual per-period upload) ──
     The report is sent whole on every change. Which entry an edit lands on, and whether
     a name that the parse missed gets appended, are decided here and stay here — the
     server stores the result rather than reimplementing the rule. */
  setStaffAccountsReport(
    periodId: string,
    fileName: string,
    entries: StaffAccountEntry[],
    by: string,
    subject: string,
  ) {
    const report: StaffAccountsReport = {
      fileName,
      uploadedBy: by,
      uploadedAt: new Date().toISOString(),
      entries,
      verified: false,
    };
    void runAction(
      periodId,
      { staffAccounts: { ...state.staffAccounts, [periodId]: report } },
      {
        type: "staffAccounts.set",
        by,
        subject: `${fileName} (${entries.length} staff) — ${subject}`,
        staffAccounts: report,
      },
    );
  },
  // Declare a period had no staff-account activity — no report to upload. Stored as an
  // empty, already-verified report (with `skipped`) so the Overview stage is complete and
  // Process does not warn about a missing report. One write, no separate verify step.
  skipStaffAccounts(periodId: string, by: string, subject: string, freeze?: unknown) {
    const existing = state.staffAccounts[periodId];
    if (existing?.verified) return;
    const now = new Date().toISOString();
    const report: StaffAccountsReport = {
      fileName: "",
      uploadedBy: by,
      uploadedAt: now,
      entries: [],
      verified: true,
      verifiedBy: by,
      verifiedAt: now,
      skipped: true,
    };
    void runAction(
      periodId,
      {
        staffAccounts: { ...state.staffAccounts, [periodId]: report },
        ...(freeze !== undefined
          ? {
              frozenStages: {
                ...state.frozenStages,
                [periodId]: { ...state.frozenStages[periodId], overview: freeze },
              },
            }
          : {}),
      },
      {
        type: "staffAccounts.set",
        by,
        ...(freeze !== undefined ? { freeze } : {}),
        subject: `no staff account activity — ${subject}`,
        staffAccounts: report,
      },
    );
  },
  // Edit an ingested on-account amount (admin verification of the parse).
  updateStaffAccountEntry(periodId: string, index: number, amount: number, by: string = "") {
    const report = state.staffAccounts[periodId];
    if (!report || report.verified) return;
    const next = { ...report, entries: report.entries.map((e, i) => (i === index ? { ...e, amount } : e)) };
    void runAction(
      periodId,
      { staffAccounts: { ...state.staffAccounts, [periodId]: next } },
      {
        type: "staffAccounts.set",
        by,
        subject: `${report.entries[index]?.name ?? `entry ${index + 1}`} set to ${amount}`,
        staffAccounts: next,
      },
    );
  },
  // Set an on-account amount by employee name — updates the matching entry or
  // adds one for a staff member the parse didn't find. Creates an empty
  // (manual, no-file) report if none exists yet so amounts can be entered
  // before any document is uploaded.
  setStaffAccountForName(periodId: string, name: string, amount: number, by: string) {
    const existing = state.staffAccounts[periodId];
    if (existing?.verified) return;
    const report: StaffAccountsReport = existing || {
      fileName: "",
      uploadedBy: by,
      uploadedAt: new Date().toISOString(),
      entries: [],
      verified: false,
    };
    const idx = report.entries.findIndex((e) => e.name === name);
    const entries =
      idx >= 0
        ? report.entries.map((e, i) => (i === idx ? { ...e, amount } : e))
        : [...report.entries, { name, amount }];
    const next = { ...report, entries };
    void runAction(
      periodId,
      { staffAccounts: { ...state.staffAccounts, [periodId]: next } },
      {
        type: "staffAccounts.set",
        by,
        subject: `${name} set to ${amount}`,
        staffAccounts: next,
      },
    );
  },
  // Lock the ingested amounts once the admin has checked them. `freeze` is the Overview
  // tab's computed output (per employee+role line) at verification; stored verbatim so the
  // verified Overview renders what was signed off, not a recomputation.
  verifyStaffAccounts(periodId: string, by: string, subject: string, freeze?: unknown) {
    const report = state.staffAccounts[periodId];
    if (!report || report.verified) return;
    const next = { ...report, verified: true, verifiedBy: by, verifiedAt: new Date().toISOString() };
    void runAction(
      periodId,
      {
        staffAccounts: { ...state.staffAccounts, [periodId]: next },
        ...(freeze !== undefined
          ? {
              frozenStages: {
                ...state.frozenStages,
                [periodId]: { ...state.frozenStages[periodId], overview: freeze },
              },
            }
          : {}),
      },
      { type: "staffAccounts.verify", by, subject, ...(freeze !== undefined ? { freeze } : {}) },
    );
  },
  // Unlock verified staff accounts so amounts can be re-checked.
  unverifyStaffAccounts(periodId: string, by: string, subject: string) {
    const report = state.staffAccounts[periodId];
    if (!report || !report.verified) return;
    const next = { ...report, verified: false, verifiedBy: undefined, verifiedAt: undefined };
    void runAction(
      periodId,
      { staffAccounts: { ...state.staffAccounts, [periodId]: next } },
      { type: "staffAccounts.unverify", by, subject },
    );
  },
  clearStaffAccountsReport(periodId: string, by: string, subject: string) {
    void runAction(
      periodId,
      { staffAccounts: withoutKey(state.staffAccounts, periodId) },
      { type: "staffAccounts.set", by, subject: `removed — ${subject}`, staffAccounts: null },
    );
  },

  /* ── Summary report (process + per-line overrides) ── */
  // Mark a period's payroll as processed (final).
  //
  // `register` is what the Summary screen computed — the figures being approved. It
  // goes with the action and is stored verbatim, because "what was paid" has to
  // survive later changes to the code that computes it.
  processPeriod(periodId: string, by: string, subject: string, register: PayrollRegister) {
    void runAction(
      periodId,
      {
        processedPeriods: {
          ...state.processedPeriods,
          [periodId]: { by, at: new Date().toISOString() },
        },
        // Drop any cached register so the Summary tab refetches the one just written
        // (a reprocess writes a new sequence); the effect reloads it on next render.
        registers: withoutKey(state.registers, periodId),
      },
      { type: "payroll.process", by, subject, register },
    );
  },
  // Reverse a processed payroll (admin re-open).
  unprocessPeriod(periodId: string, reason: string, by: string, subject: string) {
    void runAction(
      periodId,
      {
        processedPeriods: withoutKey(state.processedPeriods, periodId),
        registers: withoutKey(state.registers, periodId),
      },
      { type: "payroll.unprocess", by, subject, reason },
    );
  },
  // Merge a Summary per-line override (rate / tipPool / fohOff). Passing a
  // field as undefined leaves it unchanged; the row falls back to defaults
  // wherever no override is stored. The merge happens here and the merged value is
  // what gets stored, so the server never has to know what an override means.
  setSummaryOverride(key: string, patch: SummaryOverride, by: string, subject: string) {
    const { periodId, scoped } = splitKey(key);
    const next = { ...(state.summaryOverrides[key] || {}), ...patch };
    void runAction(
      periodId,
      { summaryOverrides: { ...state.summaryOverrides, [key]: next } },
      { type: "summaryOverride.set", by, subject, key: scoped, patch: next },
    );
  },

  /* ── Payroll control settings (server-persisted on commit/unlock) ── */
  // Set an admin-assigned holiday date for a year (cleared when blank).
  setHolidayDraft(year: number, holidayKey: string, iso: string) {
    const k = holidayDraftKey(year, holidayKey);
    const holidayDrafts = { ...state.holidayDrafts };
    if (iso) holidayDrafts[k] = iso;
    else delete holidayDrafts[k];
    commit({ ...state, holidayDrafts });
  },
  // Lock the year's holiday dates for payroll. `by` records who entered them.
  commitHolidays(year: number, holidays: CommittedHoliday[], _by: string) {
    // Clear that year's drafts now that they're committed.
    const prefix = `${year}:`;
    const holidayDrafts = Object.fromEntries(
      Object.entries(state.holidayDrafts).filter(([k]) => !k.startsWith(prefix)),
    );
    commit({
      ...state,
      committedHolidays: { ...state.committedHolidays, [String(year)]: holidays },
      holidayDrafts,
    });
    return persistRules(`Stat holidays committed (${year})`);
  },
  // Reopen a committed year's holidays for re-assignment. Dates that have
  // already passed (date < todayISO) stay LOCKED — they've been used in payroll
  // — and only future dates are released. The reopen is recorded in the audit
  // log with the supplied reason. Returns the released holiday names.
  reopenHolidays(year: number, reason: string, by: string, todayISO: string): string[] {
    const list = state.committedHolidays[String(year)] || [];
    const kept = list.filter((h) => h.date < todayISO); // already passed → stay locked
    const released = list.filter((h) => h.date >= todayISO); // future → unlock
    if (released.length === 0) return [];

    const committedHolidays = { ...state.committedHolidays };
    if (kept.length > 0) committedHolidays[String(year)] = kept;
    else delete committedHolidays[String(year)];

    const action =
      `Stat holidays reopened (${year}) — unlocked ${released.map((h) => h.name).join(", ")}` +
      (kept.length ? `; kept ${kept.map((h) => h.name).join(", ")}` : "");

    commit({ ...state, committedHolidays });
    void persistRules(`${action} — ${reason}`);
    return released.map((h) => h.name);
  },
  addBreakRule() {
    if (state.breakRulesLock) return;
    commit({ ...state, breakRules: [...state.breakRules, { overHours: 0, deductHours: 0 }] });
  },
  updateBreakRule(index: number, patch: Partial<BreakRule>) {
    if (state.breakRulesLock) return;
    commit({
      ...state,
      breakRules: state.breakRules.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    });
  },
  removeBreakRule(index: number) {
    if (state.breakRulesLock) return;
    commit({ ...state, breakRules: state.breakRules.filter((_, i) => i !== index) });
  },
  commitBreakRules(by: string) {
    commit({ ...state, breakRulesLock: { by, at: new Date().toISOString() } });
    return persistRules("Unpaid break rules committed");
  },
  unlockBreakRules(reason: string, _by: string) {
    commit({ ...state, breakRulesLock: null });
    return persistRules(`Unpaid break rules unlocked — ${reason}`);
  },

  /* ── Tip-calculation rates (commit-locks like stat holidays) ── */
  setTipRate(key: keyof TipRates, value: number) {
    if (state.tipRatesLock) return; // locked — must unlock first
    commit({ ...state, tipRates: { ...state.tipRates, [key]: value } });
  },
  commitTipRates(by: string) {
    const r = state.tipRates;
    const action =
      `Tip rates committed — CC Fee ${r.creditCardFee}%, Tip Pool ${r.tipPool}%, ` +
      `Host/Runners ${r.hostFoodRunners}%, Server→Bar ${r.serverToBar}%`;
    commit({ ...state, tipRatesLock: { by, at: new Date().toISOString() } });
    return persistRules(action);
  },
  unlockTipRates(reason: string, _by: string) {
    commit({ ...state, tipRatesLock: null });
    return persistRules(`Tip rates unlocked — ${reason}`);
  },

  /* ── FOH-eligible roles + premium (commit-locks like tip rates) ── */
  setFohRoles(roles: string[]) {
    if (state.fohRolesLock) return; // locked — must unlock first
    commit({ ...state, fohRoles: [...roles] });
  },
  // The per-hour FOH premium ($/hr) applied to the selected FOH roles. Part of
  // payConstants, edited and locked together with the FOH roles.
  setFohPremium(amount: number) {
    if (state.fohRolesLock) return; // locked — must unlock first
    commit({
      ...state,
      payConstants: { ...state.payConstants, fohPremiumPerHour: amount },
    });
  },
  commitFohRoles(by: string) {
    const action =
      `FOH roles committed — ${state.fohRoles.length ? state.fohRoles.join(", ") : "none"}` +
      ` (premium $${state.payConstants.fohPremiumPerHour}/hr)`;
    commit({ ...state, fohRolesLock: { by, at: new Date().toISOString() } });
    return persistRules(action);
  },
  unlockFohRoles(reason: string, _by: string) {
    commit({ ...state, fohRolesLock: null });
    return persistRules(`FOH roles unlocked — ${reason}`);
  },

  /* ── Salaried employees (commit-locks like FOH roles) ── */
  setSalariedEmployees(ids: string[]) {
    if (state.salariedLock) return; // locked — must unlock first
    commit({ ...state, salariedEmployeeIds: [...ids] });
  },
  commitSalaried(by: string) {
    const action = `Salaried employees committed — ${
      state.salariedEmployeeIds.length ? `${state.salariedEmployeeIds.length} employee(s)` : "none"
    }`;
    commit({ ...state, salariedLock: { by, at: new Date().toISOString() } });
    return persistRules(action);
  },
  unlockSalaried(reason: string, _by: string) {
    commit({ ...state, salariedLock: null });
    return persistRules(`Salaried employees unlocked — ${reason}`);
  },
};

export function usePayflowStore(): PayflowState {
  return useSyncExternalStore(subscribe, getSnapshot);
}

// The same state, for callers that are not components — the pull watch above already
// reads it directly, and so does anything reasoning about a pull outside a render.
//
// Unlike the hook this does NOT hydrate: subscribing is what triggers the first server
// load, and a plain read must not have that side effect.
export function getPayflowState(): PayflowState {
  return state;
}
